import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Briefcase, Clock, CheckCircle, XCircle, ChevronRight, 
  LogOut, User, ThumbsUp, ThumbsDown, DollarSign, Calendar,
  FileText, MessageSquare, History, Settings, Save, X, TrendingUp, BarChart3,
  Link2, Video, Plus, Trash2, Image, Upload, Loader2, ChevronLeft,
  Eye, Check, ChevronDown, ChevronUp, Send, AlertCircle, Mail, Tag, Search,
  MapPin, Star, GripVertical, Bell, Info, Crown, Zap, Award, PieChart,
  Globe, RefreshCw, Sliders
} from 'lucide-react';
import ChatWidget from '../components/ChatWidget';
import { toast } from '../components/ui/sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

// Info Tooltip Component
const InfoTooltip = ({ text }) => {
  const [show, setShow] = useState(false);
  
  if (!text) return null;
  
  return (
    <div className="relative inline-block ml-1">
      <button
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={(e) => { e.stopPropagation(); setShow(!show); }}
        className="w-4 h-4 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
      >
        <Info className="w-3 h-3 text-gray-400" />
      </button>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg whitespace-nowrap max-w-xs">
          <div className="whitespace-normal">{text}</div>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
        </div>
      )}
    </div>
  );
};

// Calendar Component
const CalendarView = ({ jobs, formatPrice }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const startingDayOfWeek = firstDayOfMonth.getDay() === 0 ? 6 : firstDayOfMonth.getDay() - 1; // Monday start
  const daysInMonth = lastDayOfMonth.getDate();
  
  const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 
                      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  
  // Get jobs for a specific date
  const getJobsForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return jobs.filter(job => {
      const jobDate = job.created_at?.split('T')[0];
      return jobDate === dateStr;
    });
  };
  
  // Get status color
  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#F59E0B';
      case 'accepted': return '#F59E0B';
      case 'approved': return '#22C55E';
      case 'in_progress': return '#5C63FF';
      case 'completed': return '#10B981';
      case 'rejected':
      case 'cancelled': return '#EF4444';
      default: return '#6B7280';
    }
  };
  
  const getStatusLabel = (status) => {
    switch(status) {
      case 'pending': return 'Ausstehend';
      case 'accepted': return 'Warte auf Freigabe';
      case 'approved': return 'Freigegeben';
      case 'in_progress': return 'In Bearbeitung';
      case 'completed': return 'Abgeschlossen';
      case 'rejected': return 'Abgelehnt';
      case 'cancelled': return 'Abgebrochen';
      default: return status;
    }
  };
  
  // Build calendar grid
  const calendarDays = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }
  
  const selectedJobs = selectedDate ? getJobsForDate(selectedDate) : [];
  
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Calendar */}
      <div className="lg:col-span-2">
        <div className="p-6 rounded-2xl border border-white/10" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <button 
              onClick={prevMonth}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {monthNames[month]} {year}
            </h3>
            <button 
              onClick={nextMonth}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          
          {/* Day names */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-sm text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>
          
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, index) => {
              if (!day) return <div key={index} className="h-20" />;
              
              const dayJobs = getJobsForDate(day);
              const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
              const isSelected = selectedDate === day;
              
              return (
                <button
                  key={index}
                  onClick={() => setSelectedDate(day)}
                  className={`h-20 p-1 rounded-lg border transition-all text-left flex flex-col ${
                    isSelected 
                      ? 'border-purple-500 bg-purple-500/10' 
                      : isToday 
                        ? 'border-purple-500/50 bg-white/5' 
                        : 'border-white/5 hover:border-white/20 hover:bg-white/5'
                  }`}
                >
                  <span className={`text-sm ${isToday ? 'text-purple-400 font-bold' : 'text-gray-400'}`}>
                    {day}
                  </span>
                  <div className="flex flex-wrap gap-1 mt-1 overflow-hidden">
                    {dayJobs.slice(0, 3).map((job, i) => (
                      <div 
                        key={i}
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: getStatusColor(job.status) }}
                        title={job.customer_name}
                      />
                    ))}
                    {dayJobs.length > 3 && (
                      <span className="text-xs text-gray-500">+{dayJobs.length - 3}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-6 pt-4 border-t border-white/10">
            {[
              { status: 'pending', label: 'Ausstehend' },
              { status: 'in_progress', label: 'Aktiv' },
              { status: 'completed', label: 'Abgeschlossen' },
              { status: 'rejected', label: 'Abgelehnt' },
            ].map(item => (
              <div key={item.status} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getStatusColor(item.status) }}
                />
                <span className="text-xs text-gray-400">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Selected day jobs */}
      <div>
        <div className="p-6 rounded-2xl border border-white/10 h-full" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
          <h4 className="font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {selectedDate 
              ? `${selectedDate}. ${monthNames[month]}` 
              : 'Datum auswählen'}
          </h4>
          
          {!selectedDate ? (
            <p className="text-gray-500 text-sm">Klicke auf einen Tag um die Jobs anzuzeigen.</p>
          ) : selectedJobs.length === 0 ? (
            <p className="text-gray-500 text-sm">Keine Jobs an diesem Tag.</p>
          ) : (
            <div className="space-y-3">
              {selectedJobs.map((job, i) => (
                <div 
                  key={i}
                  className="p-3 rounded-xl border border-white/10"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{job.customer_name}</p>
                      <p className="text-xs text-gray-500">
                        {job.project_details?.video_count} Video(s)
                      </p>
                    </div>
                    <span 
                      className="px-2 py-0.5 rounded-full text-xs whitespace-nowrap"
                      style={{ 
                        backgroundColor: `${getStatusColor(job.status)}20`,
                        color: getStatusColor(job.status)
                      }}
                    >
                      {getStatusLabel(job.status)}
                    </span>
                  </div>
                  <p className="text-sm font-bold mt-2" style={{ color: '#5C63FF' }}>
                    {formatPrice(job.project_details?.calculated_price)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const FreelancerDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [currentJobIndex, setCurrentJobIndex] = useState(0);
  const [activeTab, setActiveTabState] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return hash || 'pending';
  });
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    window.location.hash = tab;
  };
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  
  // Analytics
  const [analytics, setAnalytics] = useState(null);
  
  // Settings menu dropdown
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  
  // Profile editing
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editProfile, setEditProfile] = useState({
    name: '',
    phone: '',
    specialization: '',
    bio: '',
    showreel_url: '',
    portfolio_links: [],
    experience_years: 0,
    software_skills: []
  });
  const [newSkill, setNewSkill] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Team/Public Profile
  const [teamProfile, setTeamProfile] = useState(null);
  const [editTeamProfile, setEditTeamProfile] = useState({
    name: '',
    description: '',
    location: { city: '', lat: 51.1657, lng: 10.4515 },
    specializations: [],
    prices: { basePerMinute: 80 },
    // Extended profile fields
    profile_image: '',
    software_skills: [],
    portfolio_links: [],
    social_links: {
      instagram: '',
      youtube: '',
      twitter: '',
      website: ''
    }
  });
  const [newSpecialization, setNewSpecialization] = useState('');
  const [newSoftwareSkill, setNewSoftwareSkill] = useState('');
  const [newPortfolioLink, setNewPortfolioLink] = useState({ title: '', url: '' });
  const [savingTeamProfile, setSavingTeamProfile] = useState(false);
  
  // Pricing configuration
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingConfig, setPricingConfig] = useState(null);
  const [freelancerPricing, setFreelancerPricing] = useState({
    hourly_rate: 50,
    avg_minutes_per_hour: 2,
    profit_margin: 20,
    estimated_jobs_per_month: 10,
    overhead_costs: [],
    video_type_factors: {},
    addon_percentages: {},
    enabled_formats: [],
    enabled_addons: {},
    custom_prices: {},
    custom_price_types: {},
    enabled_languages: {},
    enabled_subtitle_types: {},
    custom_subtitle_prices: {},
    enabled_rush_options: {},
    custom_rush_percentages: {},
    enabled_revision_options: {},
    custom_revision_prices: {},
    enabled_video_types: {},
    custom_video_type_factors: {},
    custom_video_types: [],
    video_length_min: null,
    video_length_max: null,
    raw_material_min: null,
    raw_material_max: null,
    custom_addons: [],
  });
  const [newOverheadCost, setNewOverheadCost] = useState({ name: '', monthly_cost: 0 });
  const [savingPricing, setSavingPricing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [selectedJobForInvoice, setSelectedJobForInvoice] = useState(null);
  const [newInvoice, setNewInvoice] = useState({
    job_id: '',
    amount: 0,
    due_date: '',
    notes: ''
  });
  
  // Specialization Tags state
  const [myTags, setMyTags] = useState([]);
  const [tagCategories, setTagCategories] = useState([]);
  const [savingTags, setSavingTags] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  
  // Premium state (replaces boost)
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [premiumBilling, setPremiumBilling] = useState('monthly');
  const [premiumPackages, setPremiumPackages] = useState({});
  const [premiumAnalytics, setPremiumAnalytics] = useState(null);
  const [loadingPremium, setLoadingPremium] = useState(false);
  const [purchasingPremium, setPurchasingPremium] = useState(false);
  const [premiumMessage, setPremiumMessage] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Legacy boost state (for backwards compatibility)
  const [boostStatus, setBoostStatus] = useState(null);
  const [boostPrices, setBoostPrices] = useState({});
  const [loadingBoost, setLoadingBoost] = useState(false);
  const [purchasingBoost, setPurchasingBoost] = useState(false);
  const [boostMessage, setBoostMessage] = useState(null);
  
  // Portfolio uploads
  const [portfolioImages, setPortfolioImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    email: true,
    push: true,
    newJobs: true,
    statusUpdates: true
  });

  useEffect(() => {
    const token = localStorage.getItem('freelancer_token');
    const userData = localStorage.getItem('freelancer_user');
    
    if (!token || !userData) {
      navigate('/freelancer/login');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    fetchJobs(parsedUser.id);
    fetchAnalytics(parsedUser.id);
    fetchPortfolioImages(parsedUser.id);
    
    // Initialize edit profile
    setEditProfile({
      name: parsedUser.name || '',
      phone: parsedUser.phone || '',
      specialization: parsedUser.specialization || '',
      bio: parsedUser.bio || '',
      showreel_url: parsedUser.showreel_url || '',
      portfolio_links: parsedUser.portfolio_links || [],
      experience_years: parsedUser.experience_years || 0,
      software_skills: parsedUser.software_skills || []
    });
  }, [navigate]);

  // Auto-refresh: Poll for new data every 15 seconds
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      fetchJobs(user.id);
      fetchAnalytics(user.id);
    }, 15000);
    return () => clearInterval(interval);
  }, [user]);

  // Fetch team profile when user is loaded
  useEffect(() => {
    if (user) {
      fetchTeamProfile();
      fetchNotificationSettings(user.id);
    }
  }, [user]);

  // Handle return from Stripe checkout
  useEffect(() => {
    const boostParam = searchParams.get('boost');
    const premiumParam = searchParams.get('premium');
    const sessionId = searchParams.get('session_id');
    
    // Handle premium return
    if (premiumParam === 'success' && sessionId) {
      setActiveTab('premium');
      pollPremiumPaymentStatus(sessionId);
      setSearchParams({});
    } else if (premiumParam === 'cancelled') {
      setActiveTab('premium');
      setPremiumMessage({ type: 'error', text: 'Zahlung wurde abgebrochen.' });
      setSearchParams({});
    }
    // Handle legacy boost return
    else if (boostParam === 'success' && sessionId) {
      // Set active tab to boost
      setActiveTab('boost');
      // Poll for payment status
      pollBoostPaymentStatus(sessionId);
      // Clear URL params
      setSearchParams({});
    } else if (boostParam === 'cancelled') {
      setActiveTab('boost');
      setBoostMessage({ type: 'error', text: 'Zahlung wurde abgebrochen.' });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Poll for boost payment status
  const pollBoostPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds
    
    if (attempts >= maxAttempts) {
      setBoostMessage({ 
        type: 'error', 
        text: 'Zahlungsstatus konnte nicht überprüft werden. Bitte kontaktiere den Support.' 
      });
      return;
    }
    
    try {
      setBoostMessage({ type: 'pending', text: 'Zahlung wird überprüft...' });
      
      const response = await fetch(`${API_URL}/api/boost/checkout-status/${sessionId}`);
      if (!response.ok) throw new Error('Fehler beim Überprüfen des Zahlungsstatus');
      
      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setBoostMessage({ 
          type: 'success', 
          text: '🎉 Zahlung erfolgreich! Dein Profil ist jetzt geboostet und erscheint prominent in der Suche.' 
        });
        // Refresh boost status
        if (user?.id) fetchBoostStatus();
        return;
      } else if (data.status === 'expired') {
        setBoostMessage({ type: 'error', text: 'Zahlungssitzung abgelaufen. Bitte erneut versuchen.' });
        return;
      }
      
      // Continue polling if still pending
      setTimeout(() => pollBoostPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setBoostMessage({ type: 'error', text: 'Fehler beim Überprüfen des Zahlungsstatus.' });
    }
  };

  const fetchJobs = async (freelancerId) => {
    try {
      // Fetch all jobs for this freelancer
      const response = await fetch(`${API_URL}/api/jobs/freelancer/${freelancerId}`);
      if (response.ok) {
        const jobs = await response.json();
        
        setPendingJobs(jobs.filter(j => j.status === 'pending'));
        const waitingJobs = jobs.filter(j => j.status === 'accepted');
        const workableJobs = jobs.filter(j => ['approved', 'in_progress'].includes(j.status));
        setActiveJobs([...waitingJobs, ...workableJobs]);
        setCompletedJobs(jobs.filter(j => ['completed', 'rejected', 'cancelled'].includes(j.status)));
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async (freelancerId) => {
    try {
      const response = await fetch(`${API_URL}/api/analytics/freelancer/${freelancerId}?period=month`);
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchTeamProfile = async () => {
    if (!user) return;
    try {
      // Find the team associated with this freelancer
      const response = await fetch(`${API_URL}/api/teams`);
      if (response.ok) {
        const teams = await response.json();
        const myTeam = teams.find(t => t.freelancer_id === user.id || t.freelancer_user_id === user.id);
        if (myTeam) {
          setTeamProfile(myTeam);
          setEditTeamProfile({
            name: myTeam.name || '',
            description: myTeam.description || '',
            location: myTeam.location || { city: '', lat: 51.1657, lng: 10.4515 },
            specializations: myTeam.specializations || [],
            prices: myTeam.prices || { basePerMinute: 80 },
            // Extended profile fields
            profile_image: myTeam.profile_image || '',
            software_skills: myTeam.software_skills || [],
            portfolio_links: myTeam.portfolio_links || [],
            social_links: myTeam.social_links || {
              instagram: '',
              youtube: '',
              twitter: '',
              website: ''
            }
          });
        }
      }
    } catch (error) {
      console.error('Error fetching team profile:', error);
    }
  };

  const handleSaveTeamProfile = async () => {
    if (!teamProfile) return;
    setSavingTeamProfile(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/${teamProfile.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...teamProfile,
          ...editTeamProfile
        })
      });
      if (response.ok) {
        const updated = await response.json();
        setTeamProfile({ ...teamProfile, ...editTeamProfile });
        alert('Profil erfolgreich gespeichert!');
      }
    } catch (error) {
      console.error('Error saving team profile:', error);
      alert('Fehler beim Speichern');
    } finally {
      setSavingTeamProfile(false);
    }
  };

  const addSpecialization = () => {
    if (newSpecialization && !editTeamProfile.specializations.includes(newSpecialization)) {
      setEditTeamProfile({
        ...editTeamProfile,
        specializations: [...editTeamProfile.specializations, newSpecialization]
      });
      setNewSpecialization('');
    }
  };

  const removeSpecialization = (spec) => {
    setEditTeamProfile({
      ...editTeamProfile,
      specializations: editTeamProfile.specializations.filter(s => s !== spec)
    });
  };

  // Software Skills helpers
  const addSoftwareSkill = () => {
    if (newSoftwareSkill && !editTeamProfile.software_skills?.includes(newSoftwareSkill)) {
      setEditTeamProfile({
        ...editTeamProfile,
        software_skills: [...(editTeamProfile.software_skills || []), newSoftwareSkill]
      });
      setNewSoftwareSkill('');
    }
  };

  const removeSoftwareSkill = (skill) => {
    setEditTeamProfile({
      ...editTeamProfile,
      software_skills: (editTeamProfile.software_skills || []).filter(s => s !== skill)
    });
  };

  // Portfolio Links helpers
  const addPortfolioLink = () => {
    if (newPortfolioLink.title && newPortfolioLink.url) {
      setEditTeamProfile({
        ...editTeamProfile,
        portfolio_links: [...(editTeamProfile.portfolio_links || []), { ...newPortfolioLink }]
      });
      setNewPortfolioLink({ title: '', url: '' });
    }
  };

  const removePortfolioLink = (index) => {
    setEditTeamProfile({
      ...editTeamProfile,
      portfolio_links: (editTeamProfile.portfolio_links || []).filter((_, i) => i !== index)
    });
  };

  // Social Links helper
  const updateSocialLink = (platform, value) => {
    setEditTeamProfile({
      ...editTeamProfile,
      social_links: {
        ...(editTeamProfile.social_links || {}),
        [platform]: value
      }
    });
  };

  const fetchPortfolioImages = async (freelancerId) => {
    try {
      const response = await fetch(`${API_URL}/api/freelancer/portfolio/${freelancerId}`);
      if (response.ok) {
        const images = await response.json();
        setPortfolioImages(images);
      }
    } catch (error) {
      console.error('Error fetching portfolio images:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      alert('Nur JPG, PNG und WebP Dateien erlaubt.');
      return;
    }
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Datei zu groß. Max. 10MB erlaubt.');
      return;
    }
    
    setUploadingImage(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/freelancer/portfolio/upload/${user.id}`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setPortfolioImages(prev => [{ 
          id: data.id, 
          filename: data.filename, 
          url: data.url,
          original_name: file.name 
        }, ...prev]);
      } else {
        alert('Fehler beim Hochladen');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Fehler beim Hochladen');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePortfolioImage = async (itemId) => {
    if (!window.confirm('Bild wirklich löschen?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/freelancer/portfolio/item/${itemId}?freelancer_id=${user.id}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        setPortfolioImages(prev => prev.filter(img => img.id !== itemId));
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  // Fetch pricing configuration
  const fetchPricingConfig = async () => {
    try {
      const [globalRes, freelancerRes] = await Promise.all([
        fetch(`${API_URL}/api/pricing/config`),
        fetch(`${API_URL}/api/pricing/freelancer/${user.id}`)
      ]);
      
      if (globalRes.ok) {
        const config = await globalRes.json();
        setPricingConfig(config);
        
        // Set default enabled formats if not set
        if (!freelancerPricing.enabled_formats?.length && config.video_formats) {
          setFreelancerPricing(prev => ({
            ...prev,
            enabled_formats: config.video_formats.map(f => f.id)
          }));
        }
      }
      
      if (freelancerRes.ok) {
        const data = await freelancerRes.json();
        if (data.pricing) {
          setFreelancerPricing(prev => ({
            ...prev,
            hourly_rate: data.pricing.hourly_rate || 50,
            avg_minutes_per_hour: data.pricing.avg_minutes_per_hour || 2,
            profit_margin: data.pricing.profit_margin || 20,
            estimated_jobs_per_month: data.pricing.estimated_jobs_per_month || 10,
            overhead_costs: data.pricing.overhead_costs || [],
            video_type_factors: data.pricing.video_type_factors || {},
            addon_percentages: data.pricing.addon_percentages || {},
            enabled_formats: data.pricing.enabled_formats || prev.enabled_formats || [],
            enabled_addons: data.pricing.enabled_addons || {},
            custom_prices: data.pricing.custom_prices || {},
            custom_price_types: data.pricing.custom_price_types || {},
            enabled_languages: data.pricing.enabled_languages || {},
            enabled_subtitle_types: data.pricing.enabled_subtitle_types || {},
            custom_subtitle_prices: data.pricing.custom_subtitle_prices || {},
            enabled_rush_options: data.pricing.enabled_rush_options || {},
            custom_rush_percentages: data.pricing.custom_rush_percentages || {},
            enabled_revision_options: data.pricing.enabled_revision_options || {},
            custom_revision_prices: data.pricing.custom_revision_prices || {},
            enabled_video_types: data.pricing.enabled_video_types || {},
            custom_video_type_factors: data.pricing.custom_video_type_factors || {},
            custom_video_types: data.pricing.custom_video_types || [],
            video_length_min: data.pricing.video_length_min ?? null,
            video_length_max: data.pricing.video_length_max ?? null,
            raw_material_min: data.pricing.raw_material_min ?? null,
            raw_material_max: data.pricing.raw_material_max ?? null,
            custom_addons: data.custom_addons || [],
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching pricing config:', error);
    }
  };

  // Save pricing
  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      const { _newVideoTypeName, ...pricingToSave } = freelancerPricing;
      const response = await fetch(`${API_URL}/api/pricing/freelancer/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pricingToSave)
      });
      
      if (response.ok) {
        alert('Preise gespeichert!');
      }
    } catch (error) {
      console.error('Error saving pricing:', error);
      alert('Fehler beim Speichern');
    } finally {
      setSavingPricing(false);
    }
  };

  // Add overhead cost
  const addOverheadCost = () => {
    if (!newOverheadCost.name || newOverheadCost.monthly_cost <= 0) return;
    setFreelancerPricing(prev => ({
      ...prev,
      overhead_costs: [...prev.overhead_costs, { ...newOverheadCost, id: Date.now().toString() }]
    }));
    setNewOverheadCost({ name: '', monthly_cost: 0 });
  };

  // Remove overhead cost
  const removeOverheadCost = (id) => {
    setFreelancerPricing(prev => ({
      ...prev,
      overhead_costs: prev.overhead_costs.filter(c => c.id !== id)
    }));
  };

  // Calculate base price preview
  const calculateBasePrice = () => {
    const { hourly_rate, avg_minutes_per_hour, overhead_costs, estimated_jobs_per_month } = freelancerPricing;
    const costPerMinute = avg_minutes_per_hour > 0 ? hourly_rate / avg_minutes_per_hour : hourly_rate / 2;
    const totalMonthlyOverhead = overhead_costs.reduce((sum, c) => sum + (c.monthly_cost || 0), 0);
    const overheadPerJob = totalMonthlyOverhead / Math.max(estimated_jobs_per_month, 1);
    const overheadPerMinute = overheadPerJob / 5;
    const totalCostPerMinute = costPerMinute + overheadPerMinute;
    // Keine Gewinnmarge für Freelancer - RevoCut Marge wird separat berechnet
    return totalCostPerMinute.toFixed(2);
  };

  // Fetch invoices
  const fetchInvoices = async () => {
    if (!user?.id) return;
    setLoadingInvoices(true);
    try {
      const res = await fetch(`${API_URL}/api/freelancer/${user.id}/invoices`);
      if (res.ok) {
        const data = await res.json();
        setInvoices(data);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  // Create invoice
  const handleCreateInvoice = async () => {
    if (!selectedJobForInvoice || !newInvoice.amount) return;
    try {
      const res = await fetch(`${API_URL}/api/freelancer/${user.id}/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newInvoice,
          job_id: selectedJobForInvoice.id,
          amount: parseFloat(newInvoice.amount),
          created_at: new Date().toISOString()
        })
      });
      if (res.ok) {
        setShowCreateInvoice(false);
        setSelectedJobForInvoice(null);
        setNewInvoice({ job_id: '', amount: 0, due_date: '', notes: '' });
        fetchInvoices();
        alert('Rechnung erstellt!');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      alert('Fehler beim Erstellen der Rechnung');
    }
  };

  // Send reminder for invoice
  const handleSendReminder = async (invoice) => {
    try {
      const res = await fetch(`${API_URL}/api/freelancer/${user.id}/invoices/${invoice.id}/remind`, {
        method: 'POST'
      });
      if (res.ok) {
        alert('Zahlungserinnerung gesendet!');
      } else {
        alert('Fehler beim Senden der Erinnerung');
      }
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Fehler beim Senden');
    }
  };

  // Fetch specialization tags
  const fetchMyTags = async () => {
    if (!user?.id) return;
    try {
      const [tagsRes, categoriesRes] = await Promise.all([
        fetch(`${API_URL}/api/freelancer/${user.id}/tags`),
        fetch(`${API_URL}/api/explore/tag-categories`)
      ]);
      
      if (tagsRes.ok) {
        const data = await tagsRes.json();
        setMyTags(data.tags || []);
      }
      
      if (categoriesRes.ok) {
        const categories = await categoriesRes.json();
        setTagCategories(categories);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  // Fetch boost status
  const fetchBoostStatus = async () => {
    if (!user?.id) return;
    setLoadingBoost(true);
    try {
      const res = await fetch(`${API_URL}/api/boost/status/${user.id}`);
      if (res.ok) {
        const data = await res.json();
        setBoostStatus(data);
        setBoostPrices(data.prices || {});
      }
    } catch (error) {
      console.error('Error fetching boost status:', error);
    } finally {
      setLoadingBoost(false);
    }
  };

  // Fetch premium status and packages
  const fetchPremiumStatus = async () => {
    if (!user?.id) return;
    setLoadingPremium(true);
    try {
      const [statusRes, packagesRes] = await Promise.all([
        fetch(`${API_URL}/api/premium/status/${user.id}`),
        fetch(`${API_URL}/api/premium/packages`)
      ]);
      
      if (statusRes.ok) {
        const data = await statusRes.json();
        setPremiumStatus(data);
      }
      
      if (packagesRes.ok) {
        const packages = await packagesRes.json();
        setPremiumPackages(packages);
      }
      
      // Fetch analytics if premium
      const status = await statusRes.json().catch(() => ({}));
      if (status.is_premium) {
        const analyticsRes = await fetch(`${API_URL}/api/premium/analytics/${user.id}`);
        if (analyticsRes.ok) {
          const analytics = await analyticsRes.json();
          setPremiumAnalytics(analytics);
        }
      }
    } catch (error) {
      console.error('Error fetching premium status:', error);
    } finally {
      setLoadingPremium(false);
    }
  };

  // Purchase premium
  const handlePurchasePremium = async (premiumType) => {
    if (!user?.id) return;
    setPurchasingPremium(true);
    setPremiumMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/premium/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          freelancer_id: user.id,
          premium_type: premiumType,
          origin_url: window.location.origin
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.url;
      } else {
        const error = await res.json();
        setPremiumMessage({ type: 'error', text: error.detail || 'Fehler beim Erstellen der Bestellung' });
      }
    } catch (error) {
      console.error('Error purchasing premium:', error);
      setPremiumMessage({ type: 'error', text: 'Fehler beim Erstellen der Bestellung' });
    } finally {
      setPurchasingPremium(false);
    }
  };

  // Poll premium payment status
  const pollPremiumPaymentStatus = async (sessionId) => {
    let attempts = 0;
    const maxAttempts = 10;
    
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/premium/checkout-status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.premium_activated) {
            setPremiumMessage({ type: 'success', text: 'Premium erfolgreich aktiviert! 🎉' });
            fetchPremiumStatus();
            return true;
          }
        }
      } catch (error) {
        console.error('Error polling payment status:', error);
      }
      return false;
    };
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPremiumMessage({ type: 'error', text: 'Status konnte nicht überprüft werden. Kontaktiere uns.' });
        return;
      }
      
      const activated = await checkStatus();
      if (!activated) {
        attempts++;
        setTimeout(poll, 2000);
      }
    };
    
    poll();
  };

  // Purchase boost (legacy)
  const handlePurchaseBoost = async (boostType) => {
    if (!user?.id) return;
    setPurchasingBoost(true);
    setBoostMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/boost/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          freelancer_id: user.id,
          boost_type: boostType,
          origin_url: window.location.origin
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await res.json();
        setBoostMessage({ type: 'error', text: error.detail || 'Fehler beim Erstellen der Bestellung' });
      }
    } catch (error) {
      console.error('Error purchasing boost:', error);
      setBoostMessage({ type: 'error', text: 'Fehler beim Erstellen der Bestellung' });
    } finally {
      setPurchasingBoost(false);
    }
  };

  // Save specialization tags
  const handleSaveTags = async () => {
    if (!user?.id) return;
    setSavingTags(true);
    try {
      const res = await fetch(`${API_URL}/api/freelancer/${user.id}/tags`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(myTags)
      });
      
      if (res.ok) {
        alert('Spezialisierungen gespeichert!');
      } else {
        alert('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving tags:', error);
      alert('Fehler beim Speichern');
    } finally {
      setSavingTags(false);
    }
  };

  // Toggle tag selection
  const toggleMyTag = (tag) => {
    setMyTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag) 
        : [...prev, tag]
    );
  };

  // Fetch notification settings
  const fetchNotificationSettings = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/users/${userId}/notification-settings`);
      if (res.ok) {
        const data = await res.json();
        setNotificationSettings(data);
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
    }
  };

  // Save notification settings
  const saveNotificationSettings = async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/users/${user.id}/notification-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(notificationSettings)
      });
      
      if (res.ok) {
        toast.success('Benachrichtigungseinstellungen gespeichert!');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const [jobNotes, setJobNotes] = useState('');

  const handleJobResponse = async (jobId, response) => {
    setResponding(true);
    try {
      const notesParam = jobNotes ? `&notes=${encodeURIComponent(jobNotes)}` : '';
      const res = await fetch(`${API_URL}/api/jobs/${jobId}/respond?response=${response}${notesParam}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (res.ok) {
        toast.success(response === 'accepted' ? 'Auftrag angenommen! Warte auf Freigabe.' : 'Auftrag abgelehnt.');
        setJobNotes('');
        if (currentJobIndex < pendingJobs.length - 1) {
          setCurrentJobIndex(prev => prev + 1);
        }
        fetchJobs(user.id);
      }
    } catch (error) {
      console.error('Error responding to job:', error);
    } finally {
      setResponding(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('freelancer_token');
    localStorage.removeItem('freelancer_user');
    navigate('/freelancer/login');
  };

  const openProfileModal = () => {
    setEditProfile({
      name: user?.name || '',
      phone: user?.phone || '',
      specialization: user?.specialization || '',
      bio: user?.bio || '',
      showreel_url: user?.showreel_url || '',
      portfolio_links: user?.portfolio_links || [],
      experience_years: user?.experience_years || 0,
      software_skills: user?.software_skills || []
    });
    setNewPortfolioLink('');
    setNewSkill('');
    setShowProfileModal(true);
  };

  // Personal profile portfolio link functions - renamed to avoid conflict
  const addPersonalPortfolioLink = () => {
    if (newSkill && !editProfile.portfolio_links.includes(newSkill)) {
      setEditProfile({
        ...editProfile,
        portfolio_links: [...editProfile.portfolio_links, newSkill]
      });
    }
  };

  const removePersonalPortfolioLink = (link) => {
    setEditProfile({
      ...editProfile,
      portfolio_links: editProfile.portfolio_links.filter(l => l !== link)
    });
  };

  const addSkill = () => {
    if (newSkill && !editProfile.software_skills.includes(newSkill)) {
      setEditProfile({
        ...editProfile,
        software_skills: [...editProfile.software_skills, newSkill]
      });
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setEditProfile({
      ...editProfile,
      software_skills: editProfile.software_skills.filter(s => s !== skill)
    });
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await fetch(`${API_URL}/api/freelancer/profile/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editProfile)
      });
      
      if (response.ok) {
        const updatedUser = { ...user, ...editProfile };
        setUser(updatedUser);
        localStorage.setItem('freelancer_user', JSON.stringify(updatedUser));
        setShowProfileModal(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setSavingProfile(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price || 0);
  };

  const currentJob = pendingJobs[currentJobIndex];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Header */}
      <header className="px-6 lg:px-12 py-4 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
          <img src="/revocut-logo.png" alt="RevoCut" className="w-10 h-10" />
          <span className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>REVOCUT</span>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Back to Website Button */}
          <button 
            onClick={() => navigate('/')}
            className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white text-sm"
          >
            Zur Webseite
          </button>
          
          {/* User Info */}
          <div className="flex items-center gap-2 text-sm">
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
              style={{ backgroundColor: '#5C63FF' }}
            >
              {user?.name?.charAt(0).toUpperCase() || 'F'}
            </div>
            <span className="hidden sm:inline">{user?.name}</span>
          </div>
          
          {/* Settings Dropdown */}
          <div className="relative">
            <button 
              onClick={() => setShowSettingsMenu(!showSettingsMenu)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
              title="Einstellungen"
            >
              <Settings className="w-5 h-5" />
            </button>
            
            {showSettingsMenu && (
              <div 
                className="absolute right-0 top-full mt-2 w-48 rounded-xl border border-white/10 shadow-xl overflow-hidden"
                style={{ backgroundColor: '#1a1a1a', zIndex: 9999 }}
              >
                <button
                  onClick={() => {
                    setShowSettingsMenu(false);
                    setShowNotificationModal(true);
                  }}
                  className="w-full px-4 py-3 text-left text-sm hover:bg-white/5 flex items-center gap-2"
                >
                  <Bell className="w-4 h-4 text-yellow-400" />
                  Benachrichtigungen
                </button>
                <button
                  onClick={() => {
                    setShowSettingsMenu(false);
                    handleLogout();
                  }}
                  className="w-full px-4 py-3 text-left text-sm hover:bg-white/5 flex items-center gap-2 text-red-400"
                >
                  <LogOut className="w-4 h-4" />
                  Abmelden
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="px-6 lg:px-12 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: Clock, label: 'Neue Anfragen', value: pendingJobs.length, color: '#F59E0B' },
            { icon: Briefcase, label: 'Aktive Jobs', value: activeJobs.length, color: '#5C63FF' },
            { icon: CheckCircle, label: 'Abgeschlossen', value: analytics?.overview?.completed_jobs || completedJobs.filter(j => j.status === 'completed').length, color: '#10B981' },
            { icon: DollarSign, label: 'Gesamt verdient', value: `€${(analytics?.overview?.total_earnings || 0).toFixed(0)}`, color: '#8B5CF6' },
          ].map((stat, i) => (
            <div 
              key={i}
              className="p-6 rounded-2xl border border-white/10"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                </div>
                <span className="text-gray-400 text-sm">{stat.label}</span>
              </div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {stat.value}
              </p>
            </div>
          ))}
        </div>

        {/* Analytics Summary */}
        {analytics && (
          <div className="mb-8 p-4 rounded-xl border border-white/10 bg-white/5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-400" />
                <span className="text-sm text-gray-400">Deine Performance (30 Tage)</span>
              </div>
              <div className="flex gap-6 text-sm">
                <div>
                  <span className="text-gray-400">Annahmequote: </span>
                  <span className="text-white font-bold">{analytics.rates?.acceptance_rate || 0}%</span>
                </div>
                <div>
                  <span className="text-gray-400">Abschlussrate: </span>
                  <span className="text-white font-bold">{analytics.rates?.completion_rate || 0}%</span>
                </div>
                <div>
                  <span className="text-gray-400">Ø Job-Wert: </span>
                  <span className="text-green-400 font-bold">€{(analytics.overview?.avg_job_value || 0).toFixed(0)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {[
            { id: 'profile', label: 'Mein Profil', icon: User, count: null },
            { id: 'pending', label: 'Neue Anfragen', icon: Clock, count: pendingJobs.length },
            { id: 'active', label: 'Aktive Jobs', icon: Briefcase, count: activeJobs.length },
            { id: 'calendar', label: 'Kalender', icon: Calendar, count: null },
            { id: 'history', label: 'Historie', icon: History, count: completedJobs.length },
            { id: 'analytics', label: 'Analytics', icon: BarChart3, count: null },
            { id: 'pricing', label: 'Preise & Leistungen', icon: DollarSign, count: null },
            { id: 'tags', label: 'Spezialisierungen', icon: Tag, count: myTags.length || null },
            { id: 'premium', label: 'Premium', icon: Crown, count: premiumStatus?.is_premium ? '✓' : null },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => { 
                setActiveTab(tab.id);
                if (tab.id === 'pricing') fetchPricingConfig();
                if (tab.id === 'analytics') fetchAnalytics(user?.id);
                if (tab.id === 'invoices') fetchInvoices();
                if (tab.id === 'tags') fetchMyTags();
                if (tab.id === 'premium') fetchPremiumStatus();
                if (tab.id === 'profile') fetchTeamProfile();
              }}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-purple-500/20 text-purple-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'profile' && (
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* Left: Edit Form */}
              <div className="space-y-6">
                <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                  <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <User className="w-5 h-5 text-purple-400" />
                    Öffentliches Profil bearbeiten
                  </h3>
                  
                  {/* Team Name */}
                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2">Anzeigename / Firmenname</label>
                    <input
                      type="text"
                      value={editTeamProfile.name}
                      onChange={(e) => setEditTeamProfile({...editTeamProfile, name: e.target.value})}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                      placeholder="z.B. RevoCut Media"
                    />
                  </div>
                  
                  {/* Description */}
                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2">Beschreibung</label>
                    <textarea
                      value={editTeamProfile.description}
                      onChange={(e) => setEditTeamProfile({...editTeamProfile, description: e.target.value})}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none resize-none"
                      placeholder="Beschreibe dich und deine Arbeit..."
                    />
                  </div>
                  
                  {/* Location */}
                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2">Standort</label>
                    <input
                      type="text"
                      value={editTeamProfile.location?.city || ''}
                      onChange={(e) => setEditTeamProfile({
                        ...editTeamProfile, 
                        location: {...editTeamProfile.location, city: e.target.value}
                      })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                      placeholder="z.B. Berlin"
                    />
                  </div>
                  
                  {/* Base Price */}
                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2">Basispreis pro Minute (€)</label>
                    <input
                      type="number"
                      min="1"
                      value={editTeamProfile.prices?.basePerMinute || 80}
                      onChange={(e) => setEditTeamProfile({
                        ...editTeamProfile, 
                        prices: {...editTeamProfile.prices, basePerMinute: e.target.value === '' ? '' : Number(e.target.value)}
                      })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  
                  {/* Specializations */}
                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2">Spezialisierungen</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {editTeamProfile.specializations?.map((spec, i) => (
                        <span key={i} className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 text-sm flex items-center gap-2">
                          {spec}
                          <button onClick={() => removeSpecialization(spec)} className="hover:text-red-400">×</button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newSpecialization}
                        onChange={(e) => setNewSpecialization(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addSpecialization()}
                        className="flex-1 px-4 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                        placeholder="z.B. Motion Design"
                      />
                      <button
                        onClick={addSpecialization}
                        className="px-4 py-2 rounded-xl bg-purple-500/20 text-purple-400 hover:bg-purple-500/30"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                {/* Software & Tools */}
                <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-purple-400" />
                    Software & Tools
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">Welche Programme nutzt du?</p>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    {editTeamProfile.software_skills?.map((skill, i) => (
                      <span key={i} className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm flex items-center gap-2">
                        <Check className="w-3 h-3" />
                        {skill}
                        <button onClick={() => removeSoftwareSkill(skill)} className="hover:text-red-400 ml-1">×</button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newSoftwareSkill}
                      onChange={(e) => setNewSoftwareSkill(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addSoftwareSkill()}
                      className="flex-1 px-4 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                      placeholder="z.B. Adobe Premiere Pro"
                    />
                    <button
                      onClick={addSoftwareSkill}
                      className="px-4 py-2 rounded-xl bg-green-500/20 text-green-400 hover:bg-green-500/30"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Portfolio Links */}
                <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Video className="w-5 h-5 text-purple-400" />
                    Portfolio Links
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">Zeige deine besten Arbeiten</p>
                  
                  {/* Existing links */}
                  <div className="space-y-2 mb-4">
                    {editTeamProfile.portfolio_links?.map((link, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                        <Link2 className="w-4 h-4 text-purple-400" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{link.title}</p>
                          <p className="text-xs text-gray-500 truncate">{link.url}</p>
                        </div>
                        <button 
                          onClick={() => removePortfolioLink(i)}
                          className="p-1 hover:text-red-400 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add new link */}
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={newPortfolioLink.title}
                      onChange={(e) => setNewPortfolioLink({...newPortfolioLink, title: e.target.value})}
                      className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                      placeholder="Titel (z.B. 'Imagefilm für XYZ')"
                    />
                    <div className="flex gap-2">
                      <input
                        type="url"
                        value={newPortfolioLink.url}
                        onChange={(e) => setNewPortfolioLink({...newPortfolioLink, url: e.target.value})}
                        className="flex-1 px-4 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                        placeholder="https://youtube.com/... oder https://vimeo.com/..."
                      />
                      <button
                        onClick={addPortfolioLink}
                        disabled={!newPortfolioLink.title || !newPortfolioLink.url}
                        className="px-4 py-2 rounded-xl bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Social Media Links */}
                <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-purple-400" />
                    Social Media
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">Verknüpfe deine Social-Media-Profile</p>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1 flex items-center gap-2">
                        Instagram
                      </label>
                      <input
                        type="url"
                        value={editTeamProfile.social_links?.instagram || ''}
                        onChange={(e) => updateSocialLink('instagram', e.target.value)}
                        className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                        placeholder="https://instagram.com/deinname"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1 flex items-center gap-2">
                        YouTube
                      </label>
                      <input
                        type="url"
                        value={editTeamProfile.social_links?.youtube || ''}
                        onChange={(e) => updateSocialLink('youtube', e.target.value)}
                        className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                        placeholder="https://youtube.com/@deinkanal"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1 flex items-center gap-2">
                        Twitter / X
                      </label>
                      <input
                        type="url"
                        value={editTeamProfile.social_links?.twitter || ''}
                        onChange={(e) => updateSocialLink('twitter', e.target.value)}
                        className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                        placeholder="https://twitter.com/deinname"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1 flex items-center gap-2">
                        Website
                      </label>
                      <input
                        type="url"
                        value={editTeamProfile.social_links?.website || ''}
                        onChange={(e) => updateSocialLink('website', e.target.value)}
                        className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                        placeholder="https://deine-website.de"
                      />
                    </div>
                  </div>
                </div>

                {/* SAVE ALL Button */}
                <button
                  onClick={handleSaveTeamProfile}
                  disabled={savingTeamProfile}
                  className="w-full py-4 rounded-xl font-bold text-lg transition-all bg-purple-500 hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingTeamProfile ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Speichern...
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      Gesamtes Profil speichern
                    </>
                  )}
                </button>
                
                {/* Personal Account Settings */}
                <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                  <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-gray-400" />
                    Account-Einstellungen
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Name</label>
                      <input
                        type="text"
                        value={editProfile.name}
                        onChange={(e) => setEditProfile({...editProfile, name: e.target.value})}
                        className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Telefon</label>
                      <input
                        type="text"
                        value={editProfile.phone}
                        onChange={(e) => setEditProfile({...editProfile, phone: e.target.value})}
                        className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                        placeholder="+49..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Showreel URL</label>
                      <input
                        type="url"
                        value={editProfile.showreel_url}
                        onChange={(e) => setEditProfile({...editProfile, showreel_url: e.target.value})}
                        className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 focus:border-purple-500 focus:outline-none"
                        placeholder="https://vimeo.com/..."
                      />
                    </div>
                    <button
                      onClick={handleSaveProfile}
                      disabled={savingProfile}
                      className="w-full py-2 rounded-xl font-medium transition-all bg-white/10 hover:bg-white/20 disabled:opacity-50"
                    >
                      {savingProfile ? 'Speichern...' : 'Account speichern'}
                    </button>
                  </div>
                </div>
                
              </div>
              
              {/* Right: FULL Live Preview - Matching public profile page */}
              <div className="space-y-6">
                <div className="rounded-2xl border border-purple-500/30 p-6" style={{ backgroundColor: 'rgba(139, 92, 246, 0.05)' }}>
                  <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
                    <Eye className="w-5 h-5 text-purple-400" />
                    Live-Vorschau der öffentlichen Profilseite
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">So sehen Kunden dein vollständiges Profil:</p>
                  
                  {/* Full Profile Preview - Matching FreelancerProfile.jsx layout */}
                  <div className="rounded-xl border border-white/10 overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
                    {/* Header/Cover */}
                    <div 
                      className="h-20"
                      style={{ background: 'linear-gradient(135deg, rgba(92, 99, 255, 0.3) 0%, rgba(92, 99, 255, 0.1) 100%)' }}
                    />
                    
                    {/* Profile Info */}
                    <div className="px-4 pb-4 -mt-8">
                      <div className="flex items-end gap-3 mb-4">
                        {/* Avatar */}
                        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-2xl font-bold border-2 border-[#0a0a0a]">
                          {(editTeamProfile.name || 'R').charAt(0)}
                        </div>
                        <div className="flex-1 pt-8">
                          <h4 className="text-lg font-bold">{editTeamProfile.name || 'Dein Name'}</h4>
                          <p className="text-sm text-gray-400">{editTeamProfile.specializations?.[0] || 'Video Editor'}</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400 mb-4">
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-400" />
                          <span>{teamProfile?.rating?.toFixed(1) || '5.0'}</span>
                          <span className="text-gray-500">({teamProfile?.reviews_count || 0})</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          <span>{editTeamProfile.location?.city || 'Standort'}</span>
                        </div>
                      </div>
                      
                      {/* Tabs Preview */}
                      <div className="flex gap-1 mb-4">
                        <span className="px-3 py-1 rounded-lg bg-purple-500 text-white text-xs">Über mich</span>
                        <span className="px-3 py-1 rounded-lg bg-white/5 text-gray-400 text-xs">Portfolio</span>
                        <span className="px-3 py-1 rounded-lg bg-white/5 text-gray-400 text-xs">Bewertungen</span>
                      </div>
                      
                      {/* About Section */}
                      {editTeamProfile.description && (
                        <div className="p-3 rounded-lg bg-white/5 mb-3">
                          <h5 className="text-xs font-bold mb-1">Über mich</h5>
                          <p className="text-xs text-gray-400 line-clamp-3">{editTeamProfile.description}</p>
                        </div>
                      )}
                      
                      {/* Specializations */}
                      {editTeamProfile.specializations?.length > 0 && (
                        <div className="p-3 rounded-lg bg-white/5 mb-3">
                          <h5 className="text-xs font-bold mb-2 flex items-center gap-1">
                            <Tag className="w-3 h-3 text-purple-400" />
                            Spezialisierungen
                          </h5>
                          <div className="flex flex-wrap gap-1">
                            {editTeamProfile.specializations.map((spec, i) => (
                              <span key={i} className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs">#{spec}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Software & Tools */}
                      {editTeamProfile.software_skills?.length > 0 && (
                        <div className="p-3 rounded-lg bg-white/5 mb-3">
                          <h5 className="text-xs font-bold mb-2">Software & Tools</h5>
                          <div className="flex flex-wrap gap-1">
                            {editTeamProfile.software_skills.map((skill, i) => (
                              <span key={i} className="px-2 py-0.5 rounded-full bg-white/10 text-xs flex items-center gap-1">
                                <Check className="w-2 h-2 text-green-400" />
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Portfolio Links */}
                      {editTeamProfile.portfolio_links?.length > 0 && (
                        <div className="p-3 rounded-lg bg-white/5 mb-3">
                          <h5 className="text-xs font-bold mb-2 flex items-center gap-1">
                            <Video className="w-3 h-3 text-purple-400" />
                            Portfolio
                          </h5>
                          <div className="space-y-1">
                            {editTeamProfile.portfolio_links.slice(0, 3).map((link, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs text-gray-400">
                                <Link2 className="w-3 h-3 text-purple-400" />
                                <span className="truncate">{link.title}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Social Links */}
                      {(editTeamProfile.social_links?.instagram || editTeamProfile.social_links?.youtube || editTeamProfile.social_links?.twitter || editTeamProfile.social_links?.website) && (
                        <div className="p-3 rounded-lg bg-white/5 mb-3">
                          <h5 className="text-xs font-bold mb-2">Social Media</h5>
                          <div className="flex gap-2">
                            {editTeamProfile.social_links?.instagram && <div className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-xs">IG</div>}
                            {editTeamProfile.social_links?.youtube && <div className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-xs">YT</div>}
                            {editTeamProfile.social_links?.twitter && <div className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-xs">X</div>}
                            {editTeamProfile.social_links?.website && <div className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-xs">🌐</div>}
                          </div>
                        </div>
                      )}
                      
                      {/* CTA */}
                      <div className="flex items-center justify-between pt-2 border-t border-white/10">
                        <span className="text-sm font-bold text-purple-400">
                          €{editTeamProfile.prices?.basePerMinute || 80}<span className="text-xs text-gray-500">/Min</span>
                        </span>
                        <button className="px-3 py-1 rounded-lg bg-purple-500 text-xs">Jetzt buchen</button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Mini Card Preview */}
                <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                  <h3 className="text-sm font-bold mb-3 flex items-center gap-2 text-gray-400">
                    <Image className="w-4 h-4" />
                    Mini-Ansicht (Startseite / Karte)
                  </h3>
                  <div className="rounded-xl border border-white/10 p-3" style={{ backgroundColor: '#0a0a0a' }}>
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-lg font-bold">
                        {(editTeamProfile.name || 'R').charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-sm">{editTeamProfile.name || 'Dein Name'}</h4>
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <MapPin className="w-3 h-3" />
                          <span>{editTeamProfile.location?.city || 'Standort'}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {editTeamProfile.specializations?.slice(0, 2).map((spec, i) => (
                            <span key={i} className="px-1.5 py-0.5 rounded bg-white/10 text-[10px] text-gray-400">{spec}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Stats */}
                {teamProfile && (
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                    <h3 className="text-lg font-bold mb-4">Deine Statistiken</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-xl bg-white/5">
                        <p className="text-2xl font-bold text-purple-400">{teamProfile.rating?.toFixed(1) || '5.0'}</p>
                        <p className="text-sm text-gray-400">Bewertung</p>
                      </div>
                      <div className="p-4 rounded-xl bg-white/5">
                        <p className="text-2xl font-bold text-purple-400">{teamProfile.reviews_count || 0}</p>
                        <p className="text-sm text-gray-400">Bewertungen</p>
                      </div>
                      <div className="p-4 rounded-xl bg-white/5">
                        <p className="text-2xl font-bold text-purple-400">{teamProfile.completedProjects || 0}</p>
                        <p className="text-sm text-gray-400">Projekte</p>
                      </div>
                      <div className="p-4 rounded-xl bg-white/5">
                        <p className="text-2xl font-bold text-purple-400">Level {user?.level || 1}</p>
                        <p className="text-sm text-gray-400">Seller Level</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pending' && (
          <div className="max-w-2xl mx-auto">
            {pendingJobs.length === 0 ? (
              <div className="text-center py-20">
                <Clock className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-bold mb-2">Keine neuen Anfragen</h3>
                <p className="text-gray-400">Sobald neue Jobs verfügbar sind, erscheinen sie hier.</p>
              </div>
            ) : currentJob ? (
              <div className="space-y-6">
                {/* Progress indicator */}
                <div className="flex items-center justify-center gap-2 mb-4">
                  <span className="text-gray-400 text-sm">
                    {currentJobIndex + 1} von {pendingJobs.length}
                  </span>
                </div>

                {/* Job Card - Tinder Style */}
                <div 
                  className="p-8 rounded-3xl border border-white/10 relative overflow-hidden"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
                >
                  {/* Price Badge */}
                  <div 
                    className="absolute top-6 right-6 px-4 py-2 rounded-full font-bold"
                    style={{ backgroundColor: 'rgba(92, 99, 255, 0.2)', color: '#5C63FF' }}
                  >
                    {formatPrice(currentJob.project_details?.calculated_price)}
                  </div>

                  {/* Customer Info */}
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {currentJob.customer_name || 'Kunde'}
                    </h2>
                    <p className="text-gray-400">{currentJob.customer_email}</p>
                  </div>

                  {/* Project Details */}
                  <div className="space-y-4 mb-8">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-purple-400" />
                      <div>
                        <span className="text-gray-400 text-sm">Projekt</span>
                        <p className="font-medium">
                          {currentJob.project_details?.video_count || 1} Video(s), 
                          {currentJob.project_details?.minutes_per_video || 1} Min/Video
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-purple-400" />
                      <div>
                        <span className="text-gray-400 text-sm">Eingegangen</span>
                        <p className="font-medium">{formatDate(currentJob.created_at)}</p>
                      </div>
                    </div>

                    {currentJob.project_details?.addons && (
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-purple-400 mt-1" />
                        <div>
                          <span className="text-gray-400 text-sm">Zusatzleistungen</span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {Object.entries(currentJob.project_details.addons)
                              .filter(([_, v]) => v)
                              .map(([key]) => (
                                <span 
                                  key={key}
                                  className="px-3 py-1 rounded-full text-sm bg-white/10"
                                >
                                  {key.replace(/_/g, ' ')}
                                </span>
                              ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {currentJob.project_details?.message && (
                      <div className="flex items-start gap-3">
                        <MessageSquare className="w-5 h-5 text-purple-400 mt-1" />
                        <div>
                          <span className="text-gray-400 text-sm">Nachricht</span>
                          <p className="font-medium">{currentJob.project_details.message}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notes / Questions Field */}
                  <div className="mb-4">
                    <textarea
                      data-testid="job-notes-input"
                      value={jobNotes}
                      onChange={(e) => setJobNotes(e.target.value)}
                      placeholder="Fragen oder Anmerkungen an RevoCut (optional)..."
                      className="w-full px-4 py-3 rounded-xl text-sm bg-white/[0.05] border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#5C63FF]/50 resize-none"
                      rows={2}
                    />
                  </div>

                  {/* Action Buttons - Tinder Style */}
                  <div className="flex justify-center gap-6">
                    <button
                      onClick={() => handleJobResponse(currentJob.id, 'rejected')}
                      disabled={responding}
                      data-testid="job-reject-btn"
                      className="w-20 h-20 rounded-full flex items-center justify-center transition-all hover:scale-110 disabled:opacity-50"
                      style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)' }}
                    >
                      <ThumbsDown className="w-8 h-8 text-red-500" />
                    </button>
                    
                    <button
                      onClick={() => handleJobResponse(currentJob.id, 'accepted')}
                      disabled={responding}
                      data-testid="job-accept-btn"
                      className="w-20 h-20 rounded-full flex items-center justify-center transition-all hover:scale-110 disabled:opacity-50"
                      style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)' }}
                    >
                      <ThumbsUp className="w-8 h-8 text-green-500" />
                    </button>
                  </div>

                  <p className="text-center text-gray-500 text-sm mt-4">
                    Ablehnen oder Annehmen
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {activeTab === 'active' && (
          <div className="space-y-4">
            {activeJobs.length === 0 ? (
              <div className="text-center py-20">
                <Briefcase className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-bold mb-2">Keine aktiven Jobs</h3>
                <p className="text-gray-400">Angenommene Jobs erscheinen hier.</p>
              </div>
            ) : (
              activeJobs.map((job) => (
                <div 
                  key={job.id}
                  className={`p-6 rounded-2xl border transition-all ${
                    job.status === 'accepted' 
                      ? 'border-yellow-500/30 bg-yellow-500/[0.03]' 
                      : 'border-white/10 hover:border-purple-500/30'
                  }`}
                  style={job.status !== 'accepted' ? { backgroundColor: 'rgba(255, 255, 255, 0.02)' } : {}}
                >
                  {job.status === 'accepted' && (
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <Clock className="w-4 h-4 text-yellow-400" />
                      <span className="text-sm text-yellow-400 font-medium">Warte auf Freigabe durch RevoCut</span>
                    </div>
                  )}
                  {job.status === 'approved' && (
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/20">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-green-400 font-medium">Freigegeben - Du kannst starten!</span>
                    </div>
                  )}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{job.customer_name}</h3>
                      <p className="text-gray-400 text-sm">
                        {job.project_details?.video_count} Video(s) • {formatDate(job.created_at)}
                      </p>
                      {job.project_details?.video_type && (
                        <p className="text-gray-500 text-xs mt-1">
                          Typ: {job.project_details.video_type} • {job.project_details?.minutes_per_video} Min/Video
                        </p>
                      )}
                      {job.freelancer_response_notes && (
                        <p className="text-gray-400 text-xs mt-2 italic">
                          Deine Notiz: {job.freelancer_response_notes}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-lg" style={{ color: '#5C63FF' }}>
                          {formatPrice(job.project_details?.calculated_price)}
                        </p>
                      </div>
                      {/* Status - only changeable for approved jobs */}
                      {job.status === 'accepted' ? (
                        <div className="px-4 py-2 rounded-xl text-sm font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                          Warte auf Freigabe
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2">
                          <select
                            value={job.status}
                            onChange={async (e) => {
                              const newStatus = e.target.value;
                              const originalStatus = job.status;
                              
                              const updatedJob = { ...job, status: newStatus };
                              if (['approved', 'in_progress'].includes(newStatus)) {
                                setActiveJobs(prev => prev.map(j => j.id === job.id ? updatedJob : j));
                              } else if (newStatus === 'completed') {
                                setActiveJobs(prev => prev.filter(j => j.id !== job.id));
                                setCompletedJobs(prev => [...prev, updatedJob]);
                              }
                              
                              try {
                                const xhr = new XMLHttpRequest();
                                xhr.open('PUT', `${API_URL}/api/jobs/${job.id}/status?status=${newStatus}`, true);
                                xhr.onload = function() {
                                  if (xhr.status >= 200 && xhr.status < 300) {
                                    toast.success(`Status auf "${getStatusLabel(newStatus)}" geändert!`);
                                  } else {
                                    toast.error('Fehler beim Ändern des Status');
                                    if (['approved', 'in_progress'].includes(originalStatus)) {
                                      setActiveJobs(prev => prev.map(j => j.id === job.id ? { ...j, status: originalStatus } : j));
                                    }
                                  }
                                };
                                xhr.onerror = function() {
                                  toast.error('Netzwerkfehler beim Ändern des Status');
                                };
                                xhr.send();
                              } catch (error) {
                                console.error('Error updating status:', error);
                                toast.error('Fehler beim Ändern des Status');
                              }
                            }}
                            className={`px-4 py-2 rounded-xl font-medium text-sm cursor-pointer ${
                              job.status === 'approved' 
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                            }`}
                          >
                            <option value="in_progress">In Bearbeitung</option>
                            <option value="completed">Abgeschlossen</option>
                          </select>
                          <span className="text-xs text-gray-500 text-center">Status ändern</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            {completedJobs.length === 0 ? (
              <div className="text-center py-20">
                <History className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-bold mb-2">Keine Historie</h3>
                <p className="text-gray-400">Abgeschlossene Jobs erscheinen hier.</p>
              </div>
            ) : (
              completedJobs.map((job) => (
                <div 
                  key={job.id}
                  className="p-6 rounded-2xl border border-white/10"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-lg">{job.customer_name}</h3>
                      <p className="text-gray-400 text-sm">
                        {job.project_details?.video_count} Video(s) • {formatDate(job.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold" style={{ color: '#5C63FF' }}>
                        {formatPrice(job.project_details?.calculated_price)}
                      </p>
                      <span className={`px-3 py-1 rounded-full text-xs ${
                        job.status === 'completed' ? 'bg-green-500/20 text-green-400' : 
                        job.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {job.status === 'completed' ? 'Abgeschlossen' : 
                         job.status === 'rejected' ? 'Abgelehnt' : 'Abgebrochen'}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6" data-testid="freelancer-analytics">
            {!analytics ? (
              <div className="text-center py-20">
                <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-xl font-bold mb-2">Analytics werden geladen...</h3>
                <p className="text-gray-400">Einen Moment bitte.</p>
              </div>
            ) : (
              <>
                {/* Overview Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="rounded-2xl border border-white/10 p-5" style={{ backgroundColor: '#1a1a1a' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="w-5 h-5" style={{ color: '#5C63FF' }} />
                      <span className="text-sm text-gray-400">Gesamt Jobs</span>
                    </div>
                    <p className="text-2xl font-bold">{analytics.overview?.total_jobs || 0}</p>
                  </div>
                  <div className="rounded-2xl border border-white/10 p-5" style={{ backgroundColor: '#1a1a1a' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      <span className="text-sm text-gray-400">Abgeschlossen</span>
                    </div>
                    <p className="text-2xl font-bold text-green-400">{analytics.overview?.completed_jobs || 0}</p>
                  </div>
                  <div className="rounded-2xl border border-white/10 p-5" style={{ backgroundColor: '#1a1a1a' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-5 h-5" style={{ color: '#5C63FF' }} />
                      <span className="text-sm text-gray-400">Gesamt verdient</span>
                    </div>
                    <p className="text-2xl font-bold" style={{ color: '#5C63FF' }}>€{(analytics.overview?.total_earnings || 0).toLocaleString('de-DE', { maximumFractionDigits: 0 })}</p>
                  </div>
                  <div className="rounded-2xl border border-white/10 p-5" style={{ backgroundColor: '#1a1a1a' }}>
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      <span className="text-sm text-gray-400">Ø Job-Wert</span>
                    </div>
                    <p className="text-2xl font-bold">€{(analytics.overview?.avg_job_value || 0).toLocaleString('de-DE', { maximumFractionDigits: 0 })}</p>
                  </div>
                </div>

                {/* Rates + Monthly Chart */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Rates */}
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                    <h3 className="font-bold mb-5 flex items-center gap-2">
                      <PieChart className="w-5 h-5" style={{ color: '#5C63FF' }} />
                      Deine Raten
                    </h3>
                    <div className="space-y-5">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm text-gray-400">Annahmerate</span>
                          <span className="text-sm font-bold" style={{ color: '#5C63FF' }}>{analytics.rates?.acceptance_rate || 0}%</span>
                        </div>
                        <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${analytics.rates?.acceptance_rate || 0}%`, backgroundColor: '#5C63FF' }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm text-gray-400">Abschlussrate</span>
                          <span className="text-sm font-bold text-green-400">{analytics.rates?.completion_rate || 0}%</span>
                        </div>
                        <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 rounded-full transition-all duration-700" style={{ width: `${analytics.rates?.completion_rate || 0}%` }} />
                        </div>
                      </div>
                    </div>
                    {/* Job breakdown */}
                    <div className="mt-6 pt-5 border-t border-white/10 grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-lg font-bold text-yellow-400">{analytics.overview?.pending_jobs || 0}</p>
                        <p className="text-xs text-gray-500">Ausstehend</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold" style={{ color: '#5C63FF' }}>{analytics.overview?.active_jobs || 0}</p>
                        <p className="text-xs text-gray-500">Aktiv</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-green-400">{analytics.overview?.completed_jobs || 0}</p>
                        <p className="text-xs text-gray-500">Fertig</p>
                      </div>
                    </div>
                  </div>

                  {/* Monthly Earnings Chart */}
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                    <h3 className="font-bold mb-5 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" style={{ color: '#5C63FF' }} />
                      Einnahmen (letzte 6 Monate)
                    </h3>
                    {analytics.monthly_earnings && analytics.monthly_earnings.length > 0 ? (
                      <div className="flex items-end justify-between gap-3" style={{ height: '180px' }} data-testid="freelancer-earnings-chart">
                        {analytics.monthly_earnings.map((m, i) => {
                          const maxEarnings = Math.max(...analytics.monthly_earnings.map(x => x.earnings || 0)) || 1;
                          const barHeight = Math.max(((m.earnings || 0) / maxEarnings) * 130, 4);
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center h-full">
                              <div className="flex-1 w-full flex items-end justify-center">
                                <div 
                                  className="rounded-t-lg transition-all duration-500"
                                  style={{ 
                                    height: `${barHeight}px`,
                                    width: '100%',
                                    maxWidth: '40px',
                                    backgroundColor: '#5C63FF',
                                    opacity: m.earnings > 0 ? 1 : 0.2
                                  }}
                                />
                              </div>
                              <span className="text-xs text-gray-400 mt-2">{m.month}</span>
                              <span className="text-xs font-medium" style={{ color: m.earnings > 0 ? '#5C63FF' : '#6b7280' }}>€{(m.earnings || 0).toLocaleString('de-DE', { maximumFractionDigits: 0 })}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-10 text-gray-500">
                        <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">Noch keine Einnahmen-Daten</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Period Stats */}
                <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5" style={{ color: '#5C63FF' }} />
                    Letzter Monat
                  </h3>
                  <div className="flex items-center gap-4">
                    <div className="px-4 py-3 rounded-xl bg-white/5 border border-white/10">
                      <p className="text-xs text-gray-500">Jobs im Zeitraum</p>
                      <p className="text-xl font-bold" style={{ color: '#5C63FF' }}>{analytics.period_stats?.jobs_in_period || 0}</p>
                    </div>
                    <p className="text-sm text-gray-400">
                      {analytics.period_stats?.jobs_in_period > 0 
                        ? `Du hattest ${analytics.period_stats.jobs_in_period} Job(s) im letzten Monat. Weiter so!`
                        : 'Im letzten Monat gab es keine neuen Jobs. Aktualisiere dein Profil, um mehr Aufträge zu bekommen!'
                      }
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Calendar View */}
        {activeTab === 'calendar' && (
          <CalendarView 
            jobs={[...pendingJobs, ...activeJobs, ...completedJobs]} 
            formatPrice={formatPrice}
          />
        )}

        {/* Pricing Tab - Preise & Leistungen */}
        {activeTab === 'pricing' && (
          <div className="space-y-6">
            {/* Header with Preview Toggle */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Meine Preise & Leistungen
                </h2>
                <p className="text-sm text-gray-400">Wähle aus welche Leistungen du anbietest und setze deine Preise</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all ${
                    showPreview ? 'bg-purple-500 text-white' : 'bg-white/10 text-gray-400 hover:text-white'
                  }`}
                >
                  <Eye className="w-4 h-4" />
                  {showPreview ? 'Vorschau aktiv' : 'Vorschau'}
                </button>
                <button
                  onClick={handleSavePricing}
                  disabled={savingPricing}
                  className="btn-primary px-4 py-2 flex items-center gap-2"
                >
                  {savingPricing ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Speichern
                </button>
              </div>
            </div>

            <div className={`grid ${showPreview ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'} gap-6`}>
              {/* Left Side - Configuration */}
              <div className="space-y-6">
                {/* Base Price */}
                <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-purple-400" />
                    Basis-Kalkulation
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Stundensatz (€)</label>
                      <input
                        type="number"
                        value={freelancerPricing.hourly_rate}
                        onChange={(e) => setFreelancerPricing({...freelancerPricing, hourly_rate: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                        className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Min. Output/Stunde</label>
                      <input
                        type="number"
                        step="0.5"
                        value={freelancerPricing.avg_minutes_per_hour}
                        onChange={(e) => setFreelancerPricing({...freelancerPricing, avg_minutes_per_hour: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                        className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white"
                      />
                    </div>
                    <div className="flex items-end">
                      <div className="w-full p-3 rounded-xl bg-purple-500/10 border border-purple-500/30">
                        <p className="text-xs text-gray-400">Basispreis/Min</p>
                        <p className="text-xl font-bold" style={{ color: '#5C63FF' }}>€{calculateBasePrice()}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Video Formats - Select which to offer */}
                {pricingConfig?.video_formats && (
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                    <h3 className="font-bold mb-4">Video-Formate</h3>
                    <div className="flex flex-wrap gap-2">
                      {pricingConfig.video_formats.map((format) => {
                        const isEnabled = freelancerPricing.enabled_formats?.includes(format.id) ?? true;
                        return (
                          <button
                            key={format.id}
                            onClick={() => {
                              const current = freelancerPricing.enabled_formats || pricingConfig.video_formats.map(f => f.id);
                              const updated = isEnabled 
                                ? current.filter(id => id !== format.id)
                                : [...current, format.id];
                              setFreelancerPricing({...freelancerPricing, enabled_formats: updated});
                            }}
                            className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all ${
                              isEnabled 
                                ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50' 
                                : 'bg-white/5 text-gray-500 border border-white/10'
                            }`}
                          >
                            {isEnabled && <Check className="w-4 h-4" />}
                            {format.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Video Types */}
                {pricingConfig?.video_types && (
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Video className="w-5 h-5 text-purple-400" />
                      Videotypen
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">Welche Videotypen bearbeitest du? Du kannst auch eigene hinzufügen.</p>
                    <div className="space-y-3">
                      {pricingConfig.video_types.map((vt) => {
                        const isEnabled = freelancerPricing.enabled_video_types?.[vt.id] !== false;
                        return (
                          <div key={vt.id} className={`p-3 rounded-xl transition-all ${isEnabled ? 'bg-white/5' : 'bg-white/[0.02] opacity-50'}`}>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => {
                                  setFreelancerPricing(prev => ({
                                    ...prev,
                                    enabled_video_types: {
                                      ...prev.enabled_video_types,
                                      [vt.id]: !isEnabled
                                    }
                                  }));
                                }}
                                className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${
                                  isEnabled ? 'bg-green-500' : 'bg-white/10 border border-white/20'
                                }`}
                              >
                                {isEnabled && <Check className="w-4 h-4 text-white" />}
                              </button>
                              <div className="flex-1">
                                <p className="text-sm font-medium">{vt.name}</p>
                                {vt.description && <p className="text-xs text-gray-500">{vt.description}</p>}
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Custom video types created by this freelancer */}
                      {freelancerPricing.custom_video_types?.map((cvt, idx) => (
                        <div key={`custom-${idx}`} className="p-3 rounded-xl bg-white/5 border border-dashed border-white/15">
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-lg flex items-center justify-center bg-green-500">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium">{cvt.name}</p>
                              {cvt.description && <p className="text-xs text-gray-500">{cvt.description}</p>}
                            </div>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-400">Eigener</span>
                            <button
                              onClick={() => {
                                setFreelancerPricing(prev => ({
                                  ...prev,
                                  custom_video_types: prev.custom_video_types.filter((_, i) => i !== idx)
                                }));
                              }}
                              className="p-1 rounded hover:bg-red-500/20 text-red-400"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add custom video type */}
                    <div className="border-t border-white/10 mt-4 pt-4">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Eigenen Videotyp hinzufügen..."
                          value={freelancerPricing._newVideoTypeName || ''}
                          onChange={(e) => setFreelancerPricing(prev => ({ ...prev, _newVideoTypeName: e.target.value }))}
                          className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-sm"
                          data-testid="freelancer-new-video-type"
                        />
                        <button
                          onClick={() => {
                            const name = (freelancerPricing._newVideoTypeName || '').trim();
                            if (!name) return;
                            setFreelancerPricing(prev => ({
                              ...prev,
                              custom_video_types: [...(prev.custom_video_types || []), { name, description: '' }],
                              _newVideoTypeName: ''
                            }));
                          }}
                          className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all hover:opacity-80"
                          style={{ backgroundColor: '#5C63FF' }}
                          data-testid="freelancer-add-video-type-btn"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ========== NEW: Languages, Subtitles, Rush Delivery, Extra Revisions ========== */}
                
                {/* Languages - which languages the freelancer offers */}
                {pricingConfig?.languages && (
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Globe className="w-5 h-5 text-purple-400" />
                      Sprachen
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">Welche Sprachen bietest du für Videoschnitt an?</p>
                    <div className="flex flex-wrap gap-2">
                      {pricingConfig.languages.map((lang) => {
                        const isEnabled = freelancerPricing.enabled_languages?.[lang.id] !== false;
                        return (
                          <button
                            key={lang.id}
                            onClick={() => {
                              setFreelancerPricing(prev => ({
                                ...prev,
                                enabled_languages: {
                                  ...prev.enabled_languages,
                                  [lang.id]: !isEnabled
                                }
                              }));
                            }}
                            className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all ${
                              isEnabled
                                ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
                                : 'bg-white/5 text-gray-500 border border-white/10'
                            }`}
                          >
                            {isEnabled && <Check className="w-4 h-4" />}
                            {lang.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Subtitle Types */}
                {pricingConfig?.subtitle_types && (
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-400" />
                      Untertitel-Typen
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">Welche Untertitel-Arten bietest du an? Setze deine eigenen Preise.</p>
                    <div className="space-y-3">
                      {pricingConfig.subtitle_types.filter(s => s.id !== 'none').map((st) => {
                        const isEnabled = freelancerPricing.enabled_subtitle_types?.[st.id] !== false;
                        const customPrice = freelancerPricing.custom_subtitle_prices?.[st.id];
                        const displayPrice = customPrice !== undefined ? customPrice : st.flat_price;
                        return (
                          <div key={st.id} className={`p-3 rounded-xl transition-all ${isEnabled ? 'bg-white/5' : 'bg-white/[0.02] opacity-50'}`}>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => {
                                  setFreelancerPricing(prev => ({
                                    ...prev,
                                    enabled_subtitle_types: {
                                      ...prev.enabled_subtitle_types,
                                      [st.id]: !isEnabled
                                    }
                                  }));
                                }}
                                className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${
                                  isEnabled ? 'bg-green-500' : 'bg-white/10 border border-white/20'
                                }`}
                              >
                                {isEnabled && <Check className="w-4 h-4 text-white" />}
                              </button>
                              <div className="flex-1">
                                <p className="text-sm font-medium">{st.name}</p>
                                <p className="text-xs text-gray-500">Standard: €{st.flat_price}</p>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-blue-400 text-xs">€</span>
                                <input
                                  type="number"
                                  step="1"
                                  value={displayPrice || ''}
                                  onChange={(e) => {
                                    setFreelancerPricing(prev => ({
                                      ...prev,
                                      custom_subtitle_prices: {
                                        ...prev.custom_subtitle_prices,
                                        [st.id]: e.target.value === '' ? '' : parseFloat(e.target.value)
                                      }
                                    }));
                                  }}
                                  disabled={!isEnabled}
                                  className="w-16 px-2 py-1 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/30 text-center text-sm disabled:opacity-50"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Rush Delivery */}
                {pricingConfig?.rush_delivery_options && (
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-orange-400" />
                      Rush Delivery
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">Welche Rush-Optionen bietest du an? Setze deine eigenen Aufschläge.</p>
                    <div className="space-y-3">
                      {pricingConfig.rush_delivery_options.filter(r => r.id !== 'none').map((rd) => {
                        const isEnabled = freelancerPricing.enabled_rush_options?.[rd.id] !== false;
                        const customPct = freelancerPricing.custom_rush_percentages?.[rd.id];
                        const displayPct = customPct !== undefined ? customPct : rd.percentage;
                        return (
                          <div key={rd.id} className={`p-3 rounded-xl transition-all ${isEnabled ? 'bg-white/5' : 'bg-white/[0.02] opacity-50'}`}>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => {
                                  setFreelancerPricing(prev => ({
                                    ...prev,
                                    enabled_rush_options: {
                                      ...prev.enabled_rush_options,
                                      [rd.id]: !isEnabled
                                    }
                                  }));
                                }}
                                className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${
                                  isEnabled ? 'bg-green-500' : 'bg-white/10 border border-white/20'
                                }`}
                              >
                                {isEnabled && <Check className="w-4 h-4 text-white" />}
                              </button>
                              <div className="flex-1">
                                <p className="text-sm font-medium">{rd.name}</p>
                                <p className="text-xs text-gray-500">Standard: +{rd.percentage}%</p>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-green-400 text-xs">%</span>
                                <input
                                  type="number"
                                  step="5"
                                  value={displayPct || ''}
                                  onChange={(e) => {
                                    setFreelancerPricing(prev => ({
                                      ...prev,
                                      custom_rush_percentages: {
                                        ...prev.custom_rush_percentages,
                                        [rd.id]: e.target.value === '' ? '' : parseFloat(e.target.value)
                                      }
                                    }));
                                  }}
                                  disabled={!isEnabled}
                                  className="w-16 px-2 py-1 rounded-lg bg-green-500/10 text-green-400 border border-green-500/30 text-center text-sm disabled:opacity-50"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Extra Revisions */}
                {pricingConfig?.extra_revision_options && (
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 text-cyan-400" />
                      Extra Revisionen
                    </h3>
                    <p className="text-xs text-gray-500 mb-3">Welche Revisions-Optionen bietest du an? Setze deine eigenen Preise.</p>
                    <div className="space-y-3">
                      {pricingConfig.extra_revision_options.filter(e => e.id !== 'none').map((er) => {
                        const isEnabled = freelancerPricing.enabled_revision_options?.[er.id] !== false;
                        const customPrice = freelancerPricing.custom_revision_prices?.[er.id];
                        const displayPrice = customPrice !== undefined ? customPrice : er.flat_price;
                        return (
                          <div key={er.id} className={`p-3 rounded-xl transition-all ${isEnabled ? 'bg-white/5' : 'bg-white/[0.02] opacity-50'}`}>
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => {
                                  setFreelancerPricing(prev => ({
                                    ...prev,
                                    enabled_revision_options: {
                                      ...prev.enabled_revision_options,
                                      [er.id]: !isEnabled
                                    }
                                  }));
                                }}
                                className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${
                                  isEnabled ? 'bg-green-500' : 'bg-white/10 border border-white/20'
                                }`}
                              >
                                {isEnabled && <Check className="w-4 h-4 text-white" />}
                              </button>
                              <div className="flex-1">
                                <p className="text-sm font-medium">{er.name}</p>
                                <p className="text-xs text-gray-500">Standard: €{er.flat_price}</p>
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-blue-400 text-xs">€</span>
                                <input
                                  type="number"
                                  step="5"
                                  value={displayPrice || ''}
                                  onChange={(e) => {
                                    setFreelancerPricing(prev => ({
                                      ...prev,
                                      custom_revision_prices: {
                                        ...prev.custom_revision_prices,
                                        [er.id]: e.target.value === '' ? '' : parseFloat(e.target.value)
                                      }
                                    }));
                                  }}
                                  disabled={!isEnabled}
                                  className="w-16 px-2 py-1 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/30 text-center text-sm disabled:opacity-50"
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Video & Raw Material Ranges */}
                <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <Sliders className="w-5 h-5 text-purple-400" />
                    Videolänge & Rohmaterial
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">Welche Bereiche bietest du an?</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Min Videolänge (Min)</label>
                      <input
                        type="number"
                        value={freelancerPricing.video_length_min ?? pricingConfig?.video_length_min ?? 1}
                        onChange={(e) => setFreelancerPricing(prev => ({
                          ...prev,
                          video_length_min: e.target.value === '' ? '' : parseFloat(e.target.value)
                        }))}
                        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Max Videolänge (Min)</label>
                      <input
                        type="number"
                        value={freelancerPricing.video_length_max ?? pricingConfig?.video_length_max ?? 60}
                        onChange={(e) => setFreelancerPricing(prev => ({
                          ...prev,
                          video_length_max: e.target.value === '' ? '' : parseFloat(e.target.value)
                        }))}
                        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Min Rohmaterial (Min)</label>
                      <input
                        type="number"
                        step="0.25"
                        value={freelancerPricing.raw_material_min ?? pricingConfig?.raw_material_min ?? 0.25}
                        onChange={(e) => setFreelancerPricing(prev => ({
                          ...prev,
                          raw_material_min: e.target.value === '' ? '' : parseFloat(e.target.value)
                        }))}
                        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Max Rohmaterial (Min)</label>
                      <input
                        type="number"
                        value={freelancerPricing.raw_material_max ?? pricingConfig?.raw_material_max ?? 1200}
                        onChange={(e) => setFreelancerPricing(prev => ({
                          ...prev,
                          raw_material_max: e.target.value === '' ? '' : parseFloat(e.target.value)
                        }))}
                        className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Addon Categories - exkl. Kategorien die nur von der Plattform kontrolliert werden */}
                {pricingConfig?.addon_categories?.filter(category => 
                  category.id !== 'workflow' && // Rush Delivery & Revisionen werden zentral gesteuert
                  category.id !== 'upload_publishing' // Plattform & Publishing nur von Admin
                ).map((category) => (
                  <div key={category.id} className="rounded-2xl border border-white/10 overflow-hidden" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                    <div 
                      className="flex items-center gap-3 p-4 cursor-pointer hover:bg-white/5"
                      onClick={() => setExpandedCategories(prev => ({...prev, [category.id]: !prev[category.id]}))}
                    >
                      <div className="flex-1">
                        <p className="font-medium">{category.name}</p>
                        <p className="text-xs text-gray-500">
                          {category.addons?.filter(a => 
                            freelancerPricing.enabled_addons?.[a.id] !== false
                          ).length || 0} / {category.addons?.length || 0} aktiv
                        </p>
                      </div>
                      {expandedCategories[category.id] ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    
                    {expandedCategories[category.id] && (
                      <div className="border-t border-white/10 p-4 space-y-3">
                        {category.addons?.map((addon) => {
                          const isEnabled = freelancerPricing.enabled_addons?.[addon.id] !== false;
                          const customPrice = freelancerPricing.custom_prices?.[addon.id];
                          const displayPrice = customPrice !== undefined ? customPrice : (addon.percentage || addon.flat_price);
                          const priceType = addon.percentage !== null ? 'percentage' : 'flat';
                          
                          return (
                            <div key={addon.id} className={`p-3 rounded-xl transition-all ${isEnabled ? 'bg-white/5' : 'bg-white/[0.02] opacity-50'}`}>
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => {
                                    setFreelancerPricing(prev => ({
                                      ...prev,
                                      enabled_addons: {
                                        ...prev.enabled_addons,
                                        [addon.id]: !isEnabled
                                      }
                                    }));
                                  }}
                                  className={`w-6 h-6 rounded-lg flex items-center justify-center transition-all ${
                                    isEnabled ? 'bg-green-500' : 'bg-white/10 border border-white/20'
                                  }`}
                                >
                                  {isEnabled && <Check className="w-4 h-4 text-white" />}
                                </button>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1">
                                    <p className="text-sm font-medium">{addon.name}</p>
                                    {addon.description && <InfoTooltip text={addon.description} />}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {/* Price Type Dropdown - like Admin Dashboard */}
                                  <select
                                    value={freelancerPricing.custom_price_types?.[addon.id] || (addon.percentage !== null ? 'percent' : 'euro')}
                                    onChange={(e) => {
                                      const newType = e.target.value;
                                      const currentValue = freelancerPricing.custom_prices?.[addon.id] ?? (addon.percentage || addon.flat_price || 10);
                                      setFreelancerPricing(prev => ({
                                        ...prev,
                                        custom_price_types: {
                                          ...prev.custom_price_types,
                                          [addon.id]: newType
                                        },
                                        custom_prices: {
                                          ...prev.custom_prices,
                                          [addon.id]: currentValue
                                        }
                                      }));
                                    }}
                                    disabled={!isEnabled}
                                    className="text-xs bg-white/10 border border-white/20 rounded px-2 py-1 disabled:opacity-50"
                                  >
                                    <option value="percent">%</option>
                                    <option value="euro">€</option>
                                    <option value="multiplier">x</option>
                                  </select>
                                  <input
                                    type="number"
                                    step={
                                      (freelancerPricing.custom_price_types?.[addon.id] || (addon.percentage !== null ? 'percent' : 'euro')) === 'multiplier' 
                                        ? '0.1' 
                                        : '1'
                                    }
                                    value={displayPrice || ''}
                                    onChange={(e) => {
                                      setFreelancerPricing(prev => ({
                                        ...prev,
                                        custom_prices: {
                                          ...prev.custom_prices,
                                          [addon.id]: e.target.value === '' ? '' : parseFloat(e.target.value)
                                        }
                                      }));
                                    }}
                                    disabled={!isEnabled}
                                    className={`w-16 px-2 py-1 rounded-lg text-center text-sm disabled:opacity-50 ${
                                      (freelancerPricing.custom_price_types?.[addon.id] || (addon.percentage !== null ? 'percent' : 'euro')) === 'percent'
                                        ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                                        : (freelancerPricing.custom_price_types?.[addon.id] || (addon.percentage !== null ? 'percent' : 'euro')) === 'multiplier'
                                          ? 'bg-purple-500/10 text-purple-400 border border-purple-500/30'
                                          : 'bg-blue-500/10 text-blue-400 border border-blue-500/30'
                                    }`}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}

                {/* Eigene Leistungen / Custom Addons */}
                <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
                  <h3 className="font-bold mb-4 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-green-400" />
                    Eigene Leistungen
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">Füge eigene Services und Leistungen hinzu, die du zusätzlich anbietest.</p>
                  <div className="space-y-3 mb-4">
                    {(freelancerPricing.custom_addons || []).map((addon) => (
                      <div key={addon.id} className="p-3 rounded-xl bg-white/5 border border-white/10">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <input
                              type="text"
                              value={addon.name}
                              onChange={(e) => {
                                const updated = (freelancerPricing.custom_addons || []).map(a =>
                                  a.id === addon.id ? { ...a, name: e.target.value } : a
                                );
                                setFreelancerPricing(prev => ({ ...prev, custom_addons: updated }));
                              }}
                              className="w-full text-sm font-medium bg-transparent border-b border-transparent hover:border-white/20 focus:border-purple-500 focus:outline-none"
                              placeholder="Leistungsname"
                            />
                            <input
                              type="text"
                              value={addon.description || ''}
                              onChange={(e) => {
                                const updated = (freelancerPricing.custom_addons || []).map(a =>
                                  a.id === addon.id ? { ...a, description: e.target.value } : a
                                );
                                setFreelancerPricing(prev => ({ ...prev, custom_addons: updated }));
                              }}
                              className="w-full text-xs text-gray-500 bg-transparent border-b border-transparent hover:border-white/10 focus:border-purple-500/50 focus:outline-none mt-1"
                              placeholder="Beschreibung (optional)"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <select
                              value={addon.price_type || 'euro'}
                              onChange={(e) => {
                                const updated = (freelancerPricing.custom_addons || []).map(a =>
                                  a.id === addon.id ? { ...a, price_type: e.target.value } : a
                                );
                                setFreelancerPricing(prev => ({ ...prev, custom_addons: updated }));
                              }}
                              className="px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-xs"
                            >
                              <option value="euro">€</option>
                              <option value="percent">%</option>
                            </select>
                            <input
                              type="number"
                              step="1"
                              value={addon.price || ''}
                              onChange={(e) => {
                                const updated = (freelancerPricing.custom_addons || []).map(a =>
                                  a.id === addon.id ? { ...a, price: e.target.value === '' ? '' : parseFloat(e.target.value) } : a
                                );
                                setFreelancerPricing(prev => ({ ...prev, custom_addons: updated }));
                              }}
                              className="w-16 px-2 py-1 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/30 text-center text-sm"
                            />
                          </div>
                          <button
                            onClick={async () => {
                              if (addon.id.startsWith('custom_')) {
                                await fetch(`${API_URL}/api/pricing/freelancer/${user.id}/custom-addon/${addon.id}`, { method: 'DELETE' });
                              }
                              setFreelancerPricing(prev => ({
                                ...prev,
                                custom_addons: (prev.custom_addons || []).filter(a => a.id !== addon.id)
                              }));
                            }}
                            className="p-1 rounded hover:bg-red-500/20 text-red-400"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={async () => {
                      const res = await fetch(`${API_URL}/api/pricing/freelancer/${user.id}/custom-addon`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ name: 'Neue Leistung', price: 0, price_type: 'euro', description: '' })
                      });
                      const data = await res.json();
                      setFreelancerPricing(prev => ({
                        ...prev,
                        custom_addons: [...(prev.custom_addons || []), data.addon]
                      }));
                    }}
                    className="w-full py-3 border border-dashed border-green-500/30 rounded-xl text-sm text-green-400 hover:border-green-500 hover:bg-green-500/5 transition-all"
                  >
                    + Eigene Leistung hinzufügen
                  </button>
                </div>
              </div>

              {/* Right Side - Live Preview */}
              {showPreview && (
                <div className="lg:sticky lg:top-4 h-fit">
                  <div className="rounded-2xl border border-purple-500/30 p-6" style={{ backgroundColor: 'rgba(92, 99, 255, 0.05)' }}>
                    <div className="flex items-center gap-2 mb-4">
                      <Eye className="w-5 h-5 text-purple-400" />
                      <h3 className="font-bold">Vorschau für Kunden</h3>
                    </div>
                    
                    {/* Mock Calculator Preview */}
                    <div className="space-y-4">
                      <div className="p-4 rounded-xl bg-white/5">
                        <p className="text-sm text-gray-400 mb-1">Basispreis pro Minute</p>
                        <p className="text-2xl font-bold" style={{ color: '#5C63FF' }}>€{calculateBasePrice()}</p>
                      </div>
                      
                      <div className="p-4 rounded-xl bg-white/5">
                        <p className="text-sm text-gray-400 mb-3">Angebotene Formate</p>
                        <div className="flex flex-wrap gap-2">
                          {pricingConfig?.video_formats?.filter(f => 
                            (freelancerPricing.enabled_formats?.includes(f.id)) ?? true
                          ).map(f => (
                            <span key={f.id} className="px-2 py-1 rounded bg-white/10 text-xs">{f.name}</span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="p-4 rounded-xl bg-white/5">
                        <p className="text-sm text-gray-400 mb-3">Verfügbare Zusatzleistungen</p>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {pricingConfig?.addon_categories?.filter(cat => 
                            cat.id !== 'workflow' && cat.id !== 'upload_publishing'
                          ).map(cat => {
                            const enabledAddons = cat.addons?.filter(a => 
                              freelancerPricing.enabled_addons?.[a.id] !== false
                            );
                            if (!enabledAddons?.length) return null;
                            return (
                              <div key={cat.id}>
                                <p className="text-xs text-gray-500 mb-1">{cat.name}</p>
                                {enabledAddons.map(addon => {
                                  const price = freelancerPricing.custom_prices?.[addon.id] ?? (addon.percentage || addon.flat_price);
                                  const priceType = freelancerPricing.custom_price_types?.[addon.id] || (addon.percentage !== null ? 'percent' : 'euro');
                                  return (
                                    <div key={addon.id} className="flex justify-between text-sm py-1">
                                      <span className="text-gray-300">{addon.name}</span>
                                      <span className={
                                        priceType === 'percent' ? 'text-green-400' : 
                                        priceType === 'multiplier' ? 'text-purple-400' : 'text-blue-400'
                                      }>
                                        {priceType === 'percent' ? `+${price}%` : 
                                         priceType === 'multiplier' ? `×${price}` : `€${price}`}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                        <p className="text-sm text-green-400 mb-1">Beispielrechnung</p>
                        <p className="text-xs text-gray-400">5 Min Video + 2 Zusatzleistungen</p>
                        <p className="text-xl font-bold text-green-400 mt-2">
                          ~€{(parseFloat(calculateBasePrice()) * 5 * 1.3).toFixed(0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tags/Spezialisierungen Tab */}
        {activeTab === 'tags' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Meine Spezialisierungen
                </h2>
                <p className="text-sm text-gray-400">
                  Wähle Tags, die deine Skills und Spezialisierungen beschreiben. Diese erscheinen auf der Explore-Seite.
                </p>
              </div>
              <button
                onClick={handleSaveTags}
                disabled={savingTags}
                className="btn-primary px-4 py-2 flex items-center gap-2"
                data-testid="save-tags-btn"
              >
                {savingTags ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Speichern
              </button>
            </div>

            {/* Selected Tags */}
            {myTags.length > 0 && (
              <div className="rounded-2xl border border-purple-500/30 p-6" style={{ backgroundColor: 'rgba(92, 99, 255, 0.05)' }}>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-gray-400">
                    Ausgewählt ({myTags.length})
                  </p>
                  <button
                    onClick={() => setMyTags([])}
                    className="text-gray-400 hover:text-white text-sm transition-colors"
                  >
                    Alle entfernen
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {myTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => toggleMyTag(tag)}
                      className="px-4 py-2 rounded-xl text-sm flex items-center gap-2 text-white transition-all hover:opacity-80"
                      style={{ backgroundColor: 'rgba(92, 99, 255, 0.6)', border: '1px solid rgba(92, 99, 255, 0.8)' }}
                    >
                      #{tag}
                      <X className="w-3 h-3" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Search Tags */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                value={tagSearchQuery}
                onChange={(e) => setTagSearchQuery(e.target.value)}
                placeholder="Tags durchsuchen..."
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-purple-500/50"
                data-testid="tag-search-input"
              />
            </div>

            {/* Tag Categories */}
            <div className="space-y-4">
              {tagCategories.map(category => {
                const filteredTags = tagSearchQuery
                  ? category.tags?.filter(tag => tag.toLowerCase().includes(tagSearchQuery.toLowerCase()))
                  : category.tags;
                
                if (tagSearchQuery && (!filteredTags || filteredTags.length === 0)) return null;
                
                return (
                  <div 
                    key={category.id}
                    className="rounded-2xl border border-white/10 p-6"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
                  >
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Tag className="w-4 h-4 text-purple-400" />
                      {category.name}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {filteredTags?.map(tag => {
                        const isSelected = myTags.includes(tag);
                        return (
                          <button
                            key={tag}
                            onClick={() => toggleMyTag(tag)}
                            className={`px-4 py-2 rounded-xl text-sm transition-all ${
                              isSelected
                                ? 'text-white'
                                : 'text-gray-300 hover:text-white'
                            }`}
                            style={{ 
                              backgroundColor: isSelected 
                                ? 'rgba(92, 99, 255, 0.6)' 
                                : 'rgba(255, 255, 255, 0.05)',
                              border: isSelected 
                                ? '1px solid rgba(92, 99, 255, 0.8)' 
                                : '1px solid rgba(255, 255, 255, 0.1)'
                            }}
                            data-testid={`tag-option-${tag}`}
                          >
                            #{tag}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Info Box */}
            <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(92, 99, 255, 0.2)' }}>
                  <AlertCircle className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h4 className="font-medium mb-1">Warum sind Tags wichtig?</h4>
                  <p className="text-sm text-gray-400">
                    Deine ausgewählten Tags erscheinen auf der <strong className="text-purple-400">Explore-Seite</strong> und helfen Kunden, 
                    dich basierend auf deinen Fähigkeiten und Spezialisierungen zu finden. 
                    Je mehr relevante Tags du auswählst, desto besser wirst du gefunden!
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Premium Tab */}
        {activeTab === 'premium' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold mb-2 flex items-center gap-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <Crown className="w-6 h-6 text-purple-400" />
                Premium Membership
              </h2>
              <p className="text-gray-400">
                Schalte erweiterte Funktionen frei und maximiere deine Reichweite.
              </p>
            </div>

            {/* Premium Message */}
            {premiumMessage && (
              <div 
                className={`rounded-xl p-4 border ${
                  premiumMessage.type === 'success' 
                    ? 'bg-green-500/10 border-green-500/30 text-green-400' 
                    : premiumMessage.type === 'error'
                    ? 'bg-red-500/10 border-red-500/30 text-red-400'
                    : 'bg-blue-500/10 border-blue-500/30 text-blue-400'
                }`}
              >
                <div className="flex items-center gap-3">
                  {premiumMessage.type === 'success' && <CheckCircle className="w-5 h-5" />}
                  {premiumMessage.type === 'error' && <XCircle className="w-5 h-5" />}
                  <p>{premiumMessage.text}</p>
                </div>
              </div>
            )}

            {loadingPremium ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Current Premium Status */}
                {premiumStatus?.is_premium && (
                  <div 
                    className="rounded-2xl border border-purple-500/30 p-6"
                    style={{ backgroundColor: 'rgba(139, 92, 246, 0.05)' }}
                  >
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br from-purple-500 to-pink-500">
                          <Crown className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-purple-400 text-lg">
                              Premium {premiumStatus.premium_tier?.toUpperCase() || 'AKTIV'}
                            </h3>
                            <span className="px-2 py-0.5 rounded-full text-xs bg-purple-500/20 text-purple-300">
                              Aktiv
                            </span>
                          </div>
                          <p className="text-sm text-gray-400">
                            Noch {premiumStatus.days_remaining || 0} Tage übrig (bis {
                              premiumStatus.premium_until 
                                ? new Date(premiumStatus.premium_until).toLocaleDateString('de-DE')
                                : '-'
                            })
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Premium Analytics */}
                    {premiumAnalytics && (
                      <div className="mt-6 pt-6 border-t border-white/10">
                        <h4 className="font-medium mb-4 flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-purple-400" />
                          Deine Analytics
                        </h4>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          <div className="p-4 rounded-xl bg-white/5">
                            <p className="text-2xl font-bold text-purple-400">{premiumAnalytics.profile_views_30d}</p>
                            <p className="text-xs text-gray-400">Profilaufrufe (30 Tage)</p>
                          </div>
                          <div className="p-4 rounded-xl bg-white/5">
                            <p className="text-2xl font-bold text-green-400">{premiumAnalytics.inquiries}</p>
                            <p className="text-xs text-gray-400">Anfragen</p>
                          </div>
                          <div className="p-4 rounded-xl bg-white/5">
                            <p className="text-2xl font-bold text-yellow-400">{premiumAnalytics.jobs_completed}</p>
                            <p className="text-xs text-gray-400">Abgeschlossene Jobs</p>
                          </div>
                          <div className="p-4 rounded-xl bg-white/5">
                            <p className="text-2xl font-bold text-white">{premiumAnalytics.total_revenue}€</p>
                            <p className="text-xs text-gray-400">Gesamtumsatz</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Billing Toggle */}
                <div className="flex justify-center">
                  <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.05] border border-white/10">
                    <button
                      data-testid="dashboard-billing-monthly"
                      onClick={() => setPremiumBilling('monthly')}
                      className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                        premiumBilling === 'monthly'
                          ? 'bg-white/15 text-white'
                          : 'text-gray-400 hover:text-gray-300'
                      }`}
                    >
                      Monatlich
                    </button>
                    <button
                      data-testid="dashboard-billing-yearly"
                      onClick={() => setPremiumBilling('yearly')}
                      className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                        premiumBilling === 'yearly'
                          ? 'bg-white/15 text-white'
                          : 'text-gray-400 hover:text-gray-300'
                      }`}
                    >
                      Jährlich
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-bold">-20%</span>
                    </button>
                  </div>
                </div>

                {/* Premium Packages */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {(() => {
                    const filteredPackages = Object.entries(premiumPackages).filter(([key]) => 
                      key.endsWith(`_${premiumBilling}`)
                    );
                    return filteredPackages.map(([key, pkg]) => {
                      const isPro = pkg.tier === 'pro';
                      const isElite = pkg.tier === 'elite';
                      const isCurrentTier = premiumStatus?.premium_tier === pkg.tier;
                    
                      return (
                        <div 
                          key={key}
                          className={`rounded-2xl border p-6 transition-all relative overflow-hidden ${
                            isPro 
                              ? 'border-purple-500/50 bg-gradient-to-b from-purple-500/10 to-transparent scale-105' 
                              : isElite
                              ? 'border-white/20 bg-white/[0.02]'
                              : 'border-white/10 bg-white/[0.02]'
                          }`}
                        >
                          {isPro && (
                            <div className="absolute top-0 right-0 px-3 py-1 text-xs font-bold bg-purple-500 text-white rounded-bl-xl">
                              BELIEBT
                            </div>
                          )}
                          
                          <div className="text-center mb-6">
                            <Crown className={`w-10 h-10 mx-auto mb-3 ${isPro ? 'text-purple-400' : isElite ? 'text-purple-400' : 'text-gray-400'}`} />
                            <h3 className="text-xl font-bold mb-2">{pkg.name}</h3>
                            <div className="flex items-baseline justify-center gap-1">
                              <span className={`text-3xl font-bold ${isPro ? 'text-purple-400' : isElite ? 'text-purple-400' : 'text-white'}`}>
                                {pkg.amount}€
                              </span>
                              <span className="text-sm text-gray-500">/Monat</span>
                            </div>
                            {premiumBilling === 'yearly' && (
                              <p className="text-xs text-green-400 mt-1">Spare {Math.round((1 - pkg.amount / (premiumPackages[key.replace('yearly','monthly')]?.amount || pkg.amount)) * 100)}% pro Jahr</p>
                            )}
                          </div>
                          
                          <ul className="space-y-3 mb-6">
                            {pkg.features?.map((feature, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                                  isPro ? 'text-purple-400' : 'text-green-400'
                                }`} />
                                <span className="text-gray-300">{feature}</span>
                              </li>
                            ))}
                          </ul>
                          
                          <button
                            onClick={() => handlePurchasePremium(key)}
                            disabled={purchasingPremium || isCurrentTier}
                            className={`w-full py-3 rounded-xl font-medium transition-all ${
                              isCurrentTier
                                ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                : isPro
                                ? 'bg-purple-500 text-white hover:bg-purple-400'
                                : 'bg-white/10 hover:bg-white/20'
                            }`}
                          >
                            {isCurrentTier ? 'Aktuelles Paket' : purchasingPremium ? 'Wird geladen...' : 'Jetzt starten'}
                          </button>
                        </div>
                      );
                    });
                  })()}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Profile Edit Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md p-6 rounded-2xl border border-white/10" style={{ backgroundColor: '#1a1a1a' }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Profil bearbeiten
              </h3>
              <button onClick={() => setShowProfileModal(false)}>
                <X className="w-6 h-6 text-gray-400 hover:text-white" />
              </button>
            </div>

            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Name</label>
                <input
                  type="text"
                  value={editProfile.name}
                  onChange={(e) => setEditProfile({...editProfile, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Telefon</label>
                <input
                  type="text"
                  value={editProfile.phone}
                  onChange={(e) => setEditProfile({...editProfile, phone: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="+49 ..."
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Spezialisierung</label>
                <input
                  type="text"
                  value={editProfile.specialization}
                  onChange={(e) => setEditProfile({...editProfile, specialization: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="z.B. Video Editing, Motion Design..."
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Über mich</label>
                <textarea
                  value={editProfile.bio}
                  onChange={(e) => setEditProfile({...editProfile, bio: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  rows={2}
                  placeholder="Kurze Beschreibung..."
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Erfahrung (Jahre)</label>
                <input
                  type="number"
                  min="0"
                  value={editProfile.experience_years}
                  onChange={(e) => setEditProfile({...editProfile, experience_years: e.target.value === '' ? '' : parseInt(e.target.value)})}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                />
              </div>

              {/* Showreel */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <Video className="w-4 h-4 inline mr-1" />
                  Showreel URL
                </label>
                <input
                  type="url"
                  value={editProfile.showreel_url}
                  onChange={(e) => setEditProfile({...editProfile, showreel_url: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="https://youtube.com/... oder https://vimeo.com/..."
                />
              </div>

              {/* Portfolio Links */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <Link2 className="w-4 h-4 inline mr-1" />
                  Portfolio Links
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="url"
                    value={newPortfolioLink}
                    onChange={(e) => setNewPortfolioLink(e.target.value)}
                    className="flex-1 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    placeholder="https://..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPortfolioLink())}
                  />
                  <button
                    type="button"
                    onClick={addPortfolioLink}
                    className="px-3 py-2 rounded-xl bg-purple-500/20 text-purple-400 hover:bg-purple-500/30"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-1">
                  {editProfile.portfolio_links.map((link, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/5 text-sm">
                      <Link2 className="w-3 h-3 text-gray-500 flex-shrink-0" />
                      <a href={link} target="_blank" rel="noreferrer" className="text-purple-400 truncate flex-1 hover:text-purple-300">
                        {link}
                      </a>
                      <button onClick={() => removePortfolioLink(link)} className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Portfolio Images Upload */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">
                  <Image className="w-4 h-4 inline mr-1" />
                  Portfolio Bilder
                </label>
                
                {/* Upload Button */}
                <div className="mb-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    id="portfolio-upload"
                  />
                  <label
                    htmlFor="portfolio-upload"
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-white/20 cursor-pointer hover:border-purple-500/50 hover:bg-white/5 transition-all ${uploadingImage ? 'opacity-50 cursor-wait' : ''}`}
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                        <span className="text-sm text-gray-400">Wird hochgeladen...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 text-purple-400" />
                        <span className="text-sm text-gray-400">Bild hochladen (JPG, PNG, WebP - max. 10MB)</span>
                      </>
                    )}
                  </label>
                </div>
                
                {/* Image Grid */}
                {portfolioImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {portfolioImages.map((img) => (
                      <div key={img.id} className="relative group aspect-square rounded-lg overflow-hidden bg-white/5">
                        <img 
                          src={`${API_URL}${img.url}`} 
                          alt={img.original_name || 'Portfolio'} 
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            onClick={() => handleDeletePortfolioImage(img.id)}
                            className="p-2 rounded-full bg-red-500/80 text-white hover:bg-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {portfolioImages.length === 0 && (
                  <p className="text-xs text-gray-500 text-center py-2">Noch keine Bilder hochgeladen</p>
                )}
              </div>

              {/* Software Skills */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Software & Skills</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    className="flex-1 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors text-sm"
                    placeholder="z.B. After Effects, Premiere Pro..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  />
                  <button
                    type="button"
                    onClick={addSkill}
                    className="px-3 py-2 rounded-xl bg-purple-500/20 text-purple-400 hover:bg-purple-500/30"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {editProfile.software_skills.map((skill, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-white/10 text-white">
                      {skill}
                      <button onClick={() => removeSkill(skill)} className="text-gray-400 hover:text-red-400 ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => setShowProfileModal(false)}
                className="flex-1 py-3 rounded-xl border border-white/20 text-white hover:bg-white/5 transition-colors"
              >
                Abbrechen
              </button>
              <button 
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="flex-1 py-3 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#5C63FF' }}
              >
                {savingProfile ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Speichern
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Modal */}
      {showPricingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.9)' }}>
          <div 
            className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl border border-white/10"
            style={{ backgroundColor: '#111' }}
          >
            <div className="sticky top-0 p-6 border-b border-white/10 flex items-center justify-between z-10" style={{ backgroundColor: '#111' }}>
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Preise konfigurieren</h2>
                <p className="text-sm text-gray-500">Vollkostenkalkulation & Faktoren</p>
              </div>
              <button onClick={() => setShowPricingModal(false)} className="p-2 rounded-xl hover:bg-white/10">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Base Price Preview */}
              <div className="p-6 rounded-2xl border border-purple-500/30 bg-purple-500/10">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-400">Berechneter Basispreis pro Minute</p>
                    <p className="text-xs text-gray-500">Basierend auf deiner Vollkostenkalkulation</p>
                  </div>
                  <p className="text-3xl font-bold" style={{ color: '#5C63FF' }}>€{calculateBasePrice()}</p>
                </div>
              </div>

              {/* Vollkostenkalkulation */}
              <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.02]">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-purple-400" />
                  Vollkostenkalkulation
                </h3>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Stundensatz (€)</label>
                    <input
                      type="number"
                      value={freelancerPricing.hourly_rate}
                      onChange={(e) => setFreelancerPricing({...freelancerPricing, hourly_rate: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                      className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Ø Minuten Output/Stunde</label>
                    <input
                      type="number"
                      step="0.5"
                      value={freelancerPricing.avg_minutes_per_hour}
                      onChange={(e) => setFreelancerPricing({...freelancerPricing, avg_minutes_per_hour: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                      className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Wie viele Minuten fertiges Video produzierst du pro Stunde?</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Geschätzte Jobs/Monat</label>
                    <input
                      type="number"
                      value={freelancerPricing.estimated_jobs_per_month}
                      onChange={(e) => setFreelancerPricing({...freelancerPricing, estimated_jobs_per_month: e.target.value === '' ? '' : parseInt(e.target.value)})}
                      className="w-full px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">Für Verteilung der Gemeinkosten</p>
                  </div>
                </div>
              </div>

              {/* Gemeinkosten */}
              <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.02]">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-purple-400" />
                  Gemeinkosten (monatlich)
                </h3>
                
                <div className="space-y-2 mb-4">
                  {freelancerPricing.overhead_costs.map((cost) => (
                    <div key={cost.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                      <span className="flex-1 text-sm">{cost.name}</span>
                      <span className="text-sm font-medium">€{cost.monthly_cost}</span>
                      <button onClick={() => removeOverheadCost(cost.id)} className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {freelancerPricing.overhead_costs.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-2">Keine Gemeinkosten eingetragen</p>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="z.B. Adobe Creative Cloud"
                    value={newOverheadCost.name}
                    onChange={(e) => setNewOverheadCost({...newOverheadCost, name: e.target.value})}
                    className="flex-1 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm"
                  />
                  <input
                    type="number"
                    placeholder="€/Monat"
                    value={newOverheadCost.monthly_cost || ''}
                    onChange={(e) => setNewOverheadCost({...newOverheadCost, monthly_cost: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                    className="w-24 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm"
                  />
                  <button onClick={addOverheadCost} className="px-4 py-2 rounded-xl bg-purple-500 hover:bg-purple-600">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                
                {freelancerPricing.overhead_costs.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-white/10 flex justify-between">
                    <span className="text-sm text-gray-400">Gesamt monatlich:</span>
                    <span className="font-bold">€{freelancerPricing.overhead_costs.reduce((sum, c) => sum + (c.monthly_cost || 0), 0).toFixed(2)}</span>
                  </div>
                )}
              </div>

              {/* Save Button */}
              <div className="flex gap-3 pt-4">
                <button 
                  onClick={() => setShowPricingModal(false)}
                  className="flex-1 py-3 rounded-xl font-semibold border border-white/20 hover:border-white/40"
                >
                  Abbrechen
                </button>
                <button 
                  onClick={handleSavePricing}
                  disabled={savingPricing}
                  className="flex-1 py-3 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#5C63FF' }}
                >
                  {savingPricing ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Preise speichern
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Chat Widget */}
      <ChatWidget userId={user?.id} userName={user?.name} userType="freelancer" />

      {/* Notification Settings Modal */}
      {showNotificationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div 
            className="w-full max-w-md rounded-2xl border border-white/10 p-6"
            style={{ backgroundColor: '#1a1a1a' }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Bell className="w-5 h-5 text-yellow-400" />
                Benachrichtigungen
              </h3>
              <button onClick={() => setShowNotificationModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-3 mb-6">
              <label className="flex items-center justify-between cursor-pointer p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="font-medium text-sm">E-Mail Benachrichtigungen</p>
                    <p className="text-xs text-gray-500">Neue Jobs, Statusänderungen</p>
                  </div>
                </div>
                <input type="checkbox" checked={notificationSettings.email} onChange={(e) => setNotificationSettings({...notificationSettings, email: e.target.checked})} className="w-5 h-5 rounded accent-purple-500" />
              </label>
              
              <label className="flex items-center justify-between cursor-pointer p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-yellow-400" />
                  <div>
                    <p className="font-medium text-sm">Push-Benachrichtigungen</p>
                    <p className="text-xs text-gray-500">Popups im Browser</p>
                  </div>
                </div>
                <input type="checkbox" checked={notificationSettings.push} onChange={(e) => setNotificationSettings({...notificationSettings, push: e.target.checked})} className="w-5 h-5 rounded accent-purple-500" />
              </label>
              
              <label className="flex items-center justify-between cursor-pointer p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-3">
                  <Briefcase className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="font-medium text-sm">Neue Job-Anfragen</p>
                    <p className="text-xs text-gray-500">Sofort benachrichtigt werden</p>
                  </div>
                </div>
                <input type="checkbox" checked={notificationSettings.newJobs} onChange={(e) => setNotificationSettings({...notificationSettings, newJobs: e.target.checked})} className="w-5 h-5 rounded accent-purple-500" />
              </label>
              
              <label className="flex items-center justify-between cursor-pointer p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-5 h-5 text-purple-400" />
                  <div>
                    <p className="font-medium text-sm">Status-Updates</p>
                    <p className="text-xs text-gray-500">Bei Änderungen informiert werden</p>
                  </div>
                </div>
                <input type="checkbox" checked={notificationSettings.statusUpdates} onChange={(e) => setNotificationSettings({...notificationSettings, statusUpdates: e.target.checked})} className="w-5 h-5 rounded accent-purple-500" />
              </label>
            </div>
            
            <button
              onClick={() => { saveNotificationSettings(); setShowNotificationModal(false); }}
              className="w-full py-3 rounded-xl font-medium transition-all text-sm"
              style={{ backgroundColor: '#5C63FF' }}
            >
              Einstellungen speichern
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreelancerDashboard;
